
// Cloud templates using binary string masks
import { getActiveTheme } from '../config/seasonalFeatures';
import {
    pickFireworkEffect,
    pickVolleyDesign,
    pickVolleyPattern,
    planVolleyShots,
    resolveVolleyCharges,
    VOLLEY_DESIGNS,
    VOLLEY_PATTERNS,
    getFireworkViewingZone,
    getFireworkEffect,
    getShellCaliber,
    pickShellCaliber,
    aimIntoViewingZone,
    fitForceToViewingZone,
    fireworkUtil,
} from './fireworkEffects';

const MAX_FIREWORK_SPARKS = 1800;
const MAX_FIREWORK_SPARKS_MOBILE = 700;
/** Enough for multi-pass walks of the 9-pad field (frontage / zipper / cascade / wings). */
const MAX_VOLLEY_SHELLS = 36;
const MAX_VOLLEY_SHELLS_MOBILE = 12;

const CLOUD_TEMPLATES = {
    S1: [ // Small, short cumulus
        "000111100000",
        "001111110000",
        "011111111000",
        "001111110000"
    ],
    M1: [ // Medium, puffier cumulus
        "0000011111000000",
        "0001111111110000",
        "0011111111111000",
        "0111111111111100",
        "1111111111111110",
        "1111111111111110",
        "1111111111111110",
        "0111111111111100",
        "0011111111111000"
    ],
    CIRRUS: [ // Extra-wide wispy
        "000000111111111111111111000000",
        "000111111111111111111111110000",
        "001111111111111111111111111000",
        "000111111111111111111111110000"
    ]
};

class SnowParticle {
    constructor(x, y, fallSpeed = 1, options = {}) {
        this.x = x;
        this.y = y;
        this.active = true;     // stays true; we use settled flag for physics
        this.settled = false;
        this._lastSupportCheck = 0;
        this.fallSpeed = fallSpeed; // Individual fall speed for this particle
        
        // Snow combination properties
        this.size = 1; // Number of snow pieces (1-8)
        this.combined = false; // Track if this particle has combined this frame
        this.maxSize = 8; // Maximum size a snow particle can grow to

        // Seasonal behavior (snow / motes / leaves)
        const theme = options.theme || (typeof window !== 'undefined' && window.snowSimulation?.theme) || null;
        this.behavior = options.behavior || theme?.behavior || 'snow';
        this.combineEnabled = options.combineEnabled ?? theme?.combineEnabled ?? (this.behavior === 'snow');
        this.settleEnabled = options.settleEnabled ?? theme?.settleEnabled ?? (this.behavior === 'snow');
        this.fadeOut = options.fadeOut ?? theme?.fadeOut ?? false;
        this.tumble = options.tumble ?? theme?.tumble ?? false;
        this.palette = options.colors || theme?.colors || null;
        this.color = this.getRandomSnowColor();
        this.life = this.fadeOut ? 1 : null;
        this.vx = this.tumble ? (Math.random() - 0.5) * 2.5 : 0;
        this.spin = this.tumble ? (Math.random() - 0.5) * 0.2 : 0;
    }

    getRandomSnowColor() {
        const colors = this.palette || ['#ffffff', '#f0f8ff', '#e6f3ff', '#f5f5f5', '#e8f4f8', '#f0f0f0', '#e6e6fa', '#f8f8ff'];
        return colors[Math.floor(Math.random() * colors.length)];
    }
    
    // Adjust color based on snow chunk size with additional variation
    adjustColorForSize(size) {
        // Base color variations for different size ranges
        let baseColor;
        if (size <= 2) {
            baseColor = ['#ffffff', '#f0f8ff', '#e6f3ff', '#f5f5f5', '#e8f4f8', '#f0f0f0', '#e6e6fa', '#f8f8ff'][Math.floor(Math.random() * 8)];
        } else if (size <= 4) {
            baseColor = ['#f0f0f0', '#f5f5f5', '#fafafa', '#f8f8ff', '#f0f8ff', '#e6f3ff', '#e8f4f8', '#f0f0f0'][Math.floor(Math.random() * 8)];
        } else if (size <= 6) {
            baseColor = ['#e8e8e8', '#e6e6e6', '#f0f0f0', '#f5f5f5', '#fafafa', '#f8f8ff', '#f0f8ff', '#e6f3ff'][Math.floor(Math.random() * 8)];
        } else {
            baseColor = ['#d8d8d8', '#d0d0d0', '#e0e0e0', '#e8e8e8', '#f0f0f0', '#f5f5f5', '#fafafa', '#f8f8ff'][Math.floor(Math.random() * 8)];
        }
        
        // Add subtle random variation to prevent banding
        const variation = (Math.random() - 0.5) * 20; // Â±10 brightness variation
        return this.adjustColorBrightness(baseColor, variation);
    }
    
    // Helper method to adjust color brightness
    adjustColorBrightness(color, amount) {
        // Convert hex to RGB, adjust brightness, convert back
        const hex = color.replace('#', '');
        const r = Math.max(0, Math.min(255, parseInt(hex.substr(0, 2), 16) + amount));
        const g = Math.max(0, Math.min(255, parseInt(hex.substr(2, 2), 16) + amount));
        const b = Math.max(0, Math.min(255, parseInt(hex.substr(4, 2), 16) + amount));
        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
        }
    
    // Check for combination with snow below (stacking system)
    checkForCombination(grid, width, height, gridSize) {
        // Skip if already at max size or season disables combining
        if (!this.combineEnabled || this.size >= this.maxSize) return;
        
        // Get current grid position
        const gridX = Math.floor(this.x / gridSize);
        const gridY = Math.floor(this.y / gridSize);
        
        // Check the cell directly below us
        const belowY = gridY + 1;
        if (belowY >= Math.floor(height / gridSize)) return; // At bottom
        
        const belowCell = grid[belowY]?.[gridX];
        if (belowCell && belowCell.type === 'particle' && belowCell.data?.particle) {
            const belowParticle = belowCell.data.particle;
            
            // If the particle below has room (less than 8 pieces), combine with it
            if (belowParticle.size < this.maxSize) {
                const spaceAvailable = this.maxSize - belowParticle.size;
                const piecesToAdd = Math.min(this.size, spaceAvailable);
                
                // Add our pieces to the particle below
                belowParticle.size += piecesToAdd;
                belowParticle.color = this.adjustColorForSize(belowParticle.size);
                
                // Remove the pieces we added from ourselves
                this.size -= piecesToAdd;
                
                // If we have no pieces left, deactivate ourselves
                if (this.size <= 0) {
                    this.active = false;
                }
                
                // Mark as combined to prevent further processing
                this.combined = true;
            }
        }
        // If no snow below or it's full, we stay where we are
    }
    
    update(grid, width, height, gridSize) {
        if (!this.active) return;

        const sim = window.snowSimulation;
        const frame = sim ? sim.frameCount : 0;

        // Apply fall speed - faster particles move more frequently
        if (frame % Math.max(1, Math.round(1 / this.fallSpeed)) !== 0) {
            return; // Skip this frame for slower particles
        }

        // Fade-out motes/leaves near the ground instead of piling forever
        if (this.fadeOut && sim) {
            const fadeLine = sim.height * 0.82;
            if (this.y >= fadeLine) {
                this.life = (this.life ?? 1) - 0.08;
                if (this.life <= 0) {
                    this.active = false;
                    return;
                }
            }
        }

        // Wake up settled flakes every 4 frames if support is gone
        if (this.settled) {
            if (frame - this._lastSupportCheck >= 4) {
                this._lastSupportCheck = frame;
                const below = this.canMoveTo(this.x, this.y + gridSize, grid, width, height, gridSize);
                if (below) this.settled = false;
            }
            if (this.settled) return; // still supported; nothing to do
        }



        // Get wind effect for this frame - read local wind from wind grid
        let windOffset = 0;
        if (sim && sim.windGrid) {
            const gridX = Math.floor(this.x / gridSize);
            const gridY = Math.floor(this.y / gridSize);
            
            if (gridY >= 0 && gridY < sim.windGrid.length && gridX >= 0 && gridX < sim.windGrid[0].length) {
                const localWind = sim.windGrid[gridY][gridX].windVector;
                windOffset = localWind.x * 1.5; // Much stronger wind effect (5x stronger)
            } else {
                // Fallback to global wind if outside grid bounds
                windOffset = sim.windStrength * 1.0; // Stronger fallback too
            }
        } else {
            // Fallback to global wind if no wind grid
            windOffset = sim ? sim.windStrength * 1.0 : 0; // Stronger fallback too
        }

        if (this.tumble) {
            windOffset += this.vx;
            this.vx += this.spin * 0.15;
            this.vx *= 0.98;
        }
        
        // Check for snow combination with nearby particles
        if (this.combineEnabled) {
            this.checkForCombination(grid, width, height, gridSize);
        }
        
        // Try straight down (with wind drift)
        const targetX = this.x + windOffset;
        if (this.canMoveTo(targetX, this.y + gridSize, grid, width, height, gridSize)) {
            this.moveTo(targetX, this.y + gridSize, grid, gridSize);
            return;
        }
        
        // If blocked, compute stack height directly below
        const belowHeight = this.stackHeightBelow(grid, width, height, gridSize);

        // If stack >= 2, try to roll down the sides (left then right)
        const trySideSlide = belowHeight >= 2;

        // Down-left and down-right checks (wind only affects initial direction, not stacking)
        const dl = this.canMoveTo(this.x - gridSize, this.y + gridSize, grid, width, height, gridSize);
        const dr = this.canMoveTo(this.x + gridSize, this.y + gridSize, grid, width, height, gridSize);

        if (trySideSlide) {
            if (dl && dr) {
                // Choose randomly so piles look natural
                const dir = Math.random() < 0.5 ? -1 : 1;
                this.moveTo(this.x + dir * gridSize, this.y + gridSize, grid, gridSize);
            return;
            } else if (dl) {
                this.moveTo(this.x - gridSize, this.y + gridSize, grid, gridSize);
            return;
            } else if (dr) {
                this.moveTo(this.x + gridSize, this.y + gridSize, grid, gridSize);
            return;
            }
        } else {
            // Gentle spread even when stack < 2 (classic snow rule)
            if (dl && dr) {
                const dir = Math.random() < 0.5 ? -1 : 1;
                this.moveTo(this.x + dir * gridSize, this.y + gridSize, grid, gridSize);
                return;
            } else if (dr) {
                this.moveTo(this.x + gridSize, this.y + gridSize, grid, gridSize);
                return;
            } else if (dl) {
                this.moveTo(this.x - gridSize, this.y + gridSize, grid, gridSize);
                return;
            }
        }

        // Could not move anywhere this frame: settle if allowed, else fade away
        if (this.settleEnabled) {
            this.settled = true;
        } else if (this.fadeOut) {
            this.life = (this.life ?? 1) - 0.12;
            if (this.life <= 0) this.active = false;
        } else {
            this.active = false;
        }
    }

    // How tall is the settled pile directly under us
    stackHeightBelow(grid, width, height, gridSize) {
        let h = 0;
        const maxRows = Math.floor(height / gridSize);
        let gx = Math.floor(this.x / gridSize);
        let gy = Math.floor(this.y / gridSize);

        // Handle wrapped positions for side boundaries
        if (gx < 0) {
            gx = Math.floor(width / gridSize) + gx; // Wrap to right side
        } else if (gx >= Math.floor(width / gridSize)) {
            gx = gx - Math.floor(width / gridSize); // Wrap to left side
        }

        // Don't check beyond the map boundaries
        if (gy < 0) {
            return 0;
        }

        for (let y = gy + 1; y < maxRows; y++) {
            const cell = grid[y]?.[gx];
            if (!cell || cell.type === null) break;
            if (cell.type === 'particle' && cell.data?.particle?.settled) {
                h++;
                continue;
            }
            // Any structure below counts as blocking; stop
            if (cell.type === 'structure') break;
            // Falling particle: treat as empty for height purposes
            break;
        }
        return h;
    }

    moveTo(newX, newY, grid, gridSize) {
        const oldGX = Math.floor(this.x / gridSize);
        const oldGY = Math.floor(this.y / gridSize);
        if (grid[oldGY]?.[oldGX]?.type === 'particle') {
            grid[oldGY][oldGX] = new GridCell();
        }

        this.x = newX;
        this.y = newY;
        
        const newGX = Math.floor(this.x / gridSize);
        const newGY = Math.floor(this.y / gridSize);
        
        // Check if particle has fallen off the bottom
        const sim = window.snowSimulation;
        if (sim && newGY >= Math.floor(sim.height / gridSize)) {
            // Particle fell off the bottom - mark it for removal
            this.active = false;
            return;
        }
        
        // Handle side wrapping for continuous snowfall
        let wrappedX = newX;
        let wrappedGX = newGX;
        
        if (sim) {
            if (newX < 0) {
                // Wrap to right side
                wrappedX = sim.width + newX;
                wrappedGX = Math.floor(wrappedX / gridSize);
            } else if (newX >= sim.width) {
                // Wrap to left side
                wrappedX = newX - sim.width;
                wrappedGX = Math.floor(wrappedX / gridSize);
            }
            
            // Update position if wrapped
            if (wrappedX !== newX) {
                this.x = wrappedX;
                wrappedGX = Math.floor(wrappedX / gridSize);
            }
        }
        
        // Only add to grid if within bounds and not a structure
        if (wrappedGX >= 0 && wrappedGX < Math.floor(sim.width / gridSize) && 
            newGY >= 0 && newGY < Math.floor(sim.height / gridSize)) {
            
            if (grid[newGY]?.[wrappedGX]) {
                // Only overwrite if the cell is not a structure
                const existingCell = grid[newGY][wrappedGX];
                if (!existingCell || existingCell.type !== 'structure') {
                    grid[newGY][wrappedGX] = new GridCell('particle', { particle: this });
                }
            }
        }
    }

    canMoveTo(x, y, grid, width, height, gridSize) {
        const gridX = Math.floor(x / gridSize);
        const gridY = Math.floor(y / gridSize);
        
        // Allow falling off the bottom - snow should disappear naturally!
        if (gridY >= Math.floor(height / gridSize)) {
            return true; // Can fall off bottom
        }
        // Wrap around the sides for continuous snowfall
        if (gridX < 0 || gridX >= Math.floor(width / gridSize)) {
            return true; // Can wrap around sides
        }
        if (gridY < 0) {
            return false; // Don't go above the top
        }

        const cell = grid[gridY][gridX];

        // Empty cell: ok
        if (!cell || cell.type === null) return true;

        // Structure blocks
        if (cell.type === 'structure') return false;

        // Particle: allow if the occupant is still falling (not settled); block if settled
        if (cell.type === 'particle') {
            const p = cell.data?.particle;
            return !(p && p.settled);
        }
        
        return true;
    }
}

// Grid cell structure for storing structure information
class GridCell {
    constructor(type = null, data = {}) {
        this.type = type; // 'particle', 'structure', or null
        this.data = data; // Additional data like color, structure type, etc.
    }
}

class SnowSimulation {
    constructor(canvasId, isMobile = false, theme = null) {
        this.canvas = document.getElementById(canvasId);
        this.ctx = this.canvas.getContext('2d');
        this.width = this.canvas.width;
        this.height = this.canvas.height;
        this.isMobile = isMobile; // Store mobile flag
        this.theme = theme || getActiveTheme();
        this.behavior = this.theme.behavior || 'snow';
        this.gridSize = 4; // Fixed grid size - cannot be changed (affects map scale)
        this.grid = this.createGrid();
        this.particles = [];
        this.rockets = []; // Summer fireworks: ascending shells
        this.sparks = []; // Summer fireworks: burst particles
        this._pendingSparks = []; // flushed after update — avoids mid-loop shift corruption
        // Multi-position field: 3 batteries (L/C/R) × pads (3 desktop / 2 mobile)
        this.fireworkBatteryCount = 3;
        this.fireworkPadsPerBattery = this.isMobile ? 2 : 3;
        this.fireworkLaunchSiteCount =
            this.fireworkBatteryCount * this.fireworkPadsPerBattery;
        this.fireworkLaunchSites = [];
        this.fireworkFinaleActive = false;
        this.fireworkFinaleUntil = 0;
        this.volleysSinceFinale = 0;
        this.finaleTimeouts = [];
        this.volleyTimeouts = [];
        this.manualTimeouts = [];
        this.lastFireworkEffectName = '';
        this.lastVolleyPatternName = '';
        if (this.behavior === 'fireworks') {
            this.pickFireworkLaunchSites();
        }
        this.spawnInterval = null;
        this.manualSpawnInterval = null; // Separate interval for manual spawning
        this.spawnDelay = 50; // ms between spawns
        this.particlesPerClick = 3; // More particles per spawn for better snowfall
        this.currentSpawnX = 0;
        this.currentSpawnY = 0;
        this.snowMode = true; // Start with weather falling / fireworks launching
        this.isRunning = true;
        this.frameCount = 0;
        this.stabilityCheckInterval = 4; // Check stability every 4 frames
        // Randomize snowflake properties on startup for variety
        this.smallestPieces = Math.floor(Math.random() * 4) + 1; // 1 to 4 pieces
        this.biggestPieces = Math.floor(Math.random() * 9) + 4; // 4 to 12 pieces
        this.minFallSpeed = 0.1 + Math.random() * 0.9; // 0.1 to 1.0 speed
        this.maxFallSpeed = 0.5 + Math.random() * 5.5; // 0.5 to 6.0 speed
        
        // Ensure valid relationships
        if (this.biggestPieces < this.smallestPieces) {
            this.biggestPieces = this.smallestPieces;
        }
        if (this.maxFallSpeed < this.minFallSpeed) {
            this.maxFallSpeed = this.minFallSpeed;
        }
        
        // Randomized values set
        // Random default wind: between -3 and 3
        this.windStrength = (Math.random() - 0.5) * 6; // Random strength -3 to 3
        
        // Storm variance properties
        this.stormVarianceEnabled = this.behavior !== 'fireworks'; // quieter variance for fireworks
        this.windVariance = 2.5; // How much wind can vary
        this.snowfallVariance = 200; // How much snowfall rate can vary
        this.varianceSpeed = 3; // How fast values change between extremes
        this.baseWindStrength = this.windStrength; // Store base wind for variance calculations
        
        // Layer system scaffolding (background/midground/foreground)
        this.layers = {
            background: [],
            midground: [],
            foreground: []
        };

        // Wind grid scaffolding for per-cell wind vectors and future physics
        this.windGrid = this.createWindGrid();
        this.showWindArrows = false; // Visualization toggle (wired later)

        // Spawn rate: theme default or randomized (winter-style)
        if (this.theme.defaultSpawnRate != null) {
            this.baseFlakesPerMinute = this.theme.defaultSpawnRate;
        } else {
            const min = this.theme.spawnRateMin || 100;
            const max = this.theme.spawnRateMax || 3800;
            this.baseFlakesPerMinute = Math.floor(Math.random() * (max - min + 1)) + min;
        }
        this.flakesPerMinute = this.baseFlakesPerMinute; // Current active snowfall rate (can vary from base)
        this.currentSpawnRate = this.baseFlakesPerMinute; // Current spawn interval rate (to avoid constant updates)
        this.totalFlakesSpawned = 0; // Total individual flakes ever spawned
        
        // Smooth dot movement properties
        this.smoothDotX = 0;
        this.smoothDotY = 0;
        this.smoothDotTargetX = 0;
        this.smoothDotTargetY = 0;
        this.smoothDotSpeed = 0.1; // How fast the dot moves (0.1 = smooth, 1.0 = instant)
        
        // Cloud control properties - total of 1-9 clouds randomly distributed across types
        // On mobile, only use CIRRUS clouds (high altitude); summer uses fewer/dimmer clouds
        const summerSparse = this.behavior === 'fireworks';
        if (this.isMobile) {
            const cirrusCount = summerSparse ? 0 : Math.floor(Math.random() * 2) + 1;
            this.cloudSettings = {
                S1: { targetCount: 0, minY: 120, maxY: 160, currentCount: 0 },
                M1: { targetCount: 0, minY: 120, maxY: 160, currentCount: 0 },
                CIRRUS: { targetCount: cirrusCount, minY: 40, maxY: 80, currentCount: 0 }
            };
        } else if (summerSparse) {
            this.cloudSettings = {
                S1: { targetCount: 0, minY: 120, maxY: 160, currentCount: 0 },
                M1: { targetCount: 0, minY: 120, maxY: 160, currentCount: 0 },
                CIRRUS: { targetCount: Math.floor(Math.random() * 2), minY: 30, maxY: 70, currentCount: 0 }
            };
        } else {
            const totalClouds = Math.floor(Math.random() * 9) + 1; // 1-9 total clouds
            const s1Count = Math.floor(Math.random() * (totalClouds + 1)); // 0 to totalClouds
            const m1Count = Math.floor(Math.random() * (totalClouds - s1Count + 1)); // 0 to remaining
            const cirrusCount = totalClouds - s1Count - m1Count; // Remaining clouds
            
            this.cloudSettings = {
                S1: { targetCount: s1Count, minY: 120, maxY: 160, currentCount: 0 },
                M1: { targetCount: m1Count, minY: 120, maxY: 160, currentCount: 0 },
                CIRRUS: { targetCount: cirrusCount, minY: 40, maxY: 80, currentCount: 0 }
            };
        }
        
        this.clouds = []; // Separate array for clouds so they don't interfere with snow physics
        
        this.setupEventListeners();
        this.setupControls();
        this.createDefaultStructures();
        this.start();
        this.startSnow(); // Start weather immediately
        
        // Initialize snowfall display
        this.updateSnowfallDisplay(this.baseFlakesPerMinute);
        
        // Initialize flakes per minute display with just the spawn rate
        const flakesPerMinuteValue = document.getElementById('flakesPerMinuteValue');
        if (flakesPerMinuteValue) {
            flakesPerMinuteValue.textContent = this.baseFlakesPerMinute;
        }
        
        // Initialize estimated flakes per minute stat
        setTimeout(() => this.updateEstimatedFlakesStat(), 100);
        
        // Initialize smooth dot position
        this.smoothDotX = this.width / 2;
        this.smoothDotY = this.height / 2;
        this.smoothDotTargetX = this.smoothDotX;
        this.smoothDotTargetY = this.smoothDotY;
        
        // Prime the wind grid based on initial wind
        if (this.windGrid) {
            this.updateWindGrid();
        }

        // Make simulation globally accessible for snow particle collision detection
        window.snowSimulation = this;
    }

    // Create a simple wind grid initialized to zero vectors
    createWindGrid() {
        const cols = Math.floor(this.width / this.gridSize);
        const rows = Math.floor(this.height / this.gridSize);
        const windGrid = new Array(rows);
        for (let y = 0; y < rows; y++) {
            windGrid[y] = new Array(cols);
            for (let x = 0; x < cols; x++) {
                windGrid[y][x] = {
                    windVector: { x: 0, y: 0 },
                    type: 'air',
                    temperature: 70,
                    humidity: 0.5
                };
            }
        }
        return windGrid;
    }

    // Update wind grid with turbulence effects
    updateWindGrid() {
        if (!this.windGrid) return;
        
        const baseWindX = this.windStrength * 0.5; // Much stronger base wind effect
        
        for (let y = 0; y < this.windGrid.length; y++) {
            for (let x = 0; x < this.windGrid[0].length; x++) {
                const cell = this.windGrid[y][x];
                
                // Apply base wind with much stronger variation
                cell.windVector.x = baseWindX + (Math.random() - 0.5) * 2.0; // 10x stronger variation
                cell.windVector.y = (Math.random() - 0.5) * 1.0; // 10x stronger vertical variation
                
                // Add much stronger turbulence around buildings
                const gridCell = this.grid[y]?.[x];
                if (gridCell && gridCell.type === 'structure') {
                    cell.windVector.x += (Math.random() - 0.5) * 3.0; // 6x stronger building turbulence
                    cell.windVector.y += (Math.random() - 0.5) * 2.0; // 6x stronger building turbulence
                }
            }
        }
    }

    paintCloud(ctx, gridSize, xCells, yCells, templateName, palette) {
        // Paint a cloud using a bitmap template
        const template = CLOUD_TEMPLATES[templateName];
        if (!template) return;
        
        // Loop over each row of the template
        for (let row = 0; row < template.length; row++) {
            const rowString = template[row];
            
            // For each "1" cell, fill a rectangle
            for (let col = 0; col < rowString.length; col++) {
                if (rowString[col] === '1') {
                    const x = (xCells + col) * gridSize;
                    const y = (yCells + row) * gridSize;
                    
                    // Shade rows: top third, middle third, bottom third
                    let color;
                    if (row < template.length / 3) {
                        color = palette.top || '#FFFFFF';
                    } else if (row < (template.length * 2) / 3) {
                        color = palette.middle || '#F6F6F6';
                    } else {
                        color = palette.bottom || '#ECECEC';
                    }
                    
                    ctx.fillStyle = color;
                    ctx.fillRect(x, y, gridSize, gridSize);
                }
            }
        }
    }

    createGrid() {
        const cols = Math.floor(this.width / this.gridSize);
        const rows = Math.floor(this.height / this.gridSize);
        const grid = [];
        
        for (let y = 0; y < rows; y++) {
            grid[y] = [];
            for (let x = 0; x < cols; x++) {
                grid[y][x] = new GridCell();
            }
        }
        
        return grid;
    }

    createDefaultStructures() {
        // Ground level - 10 pixels thick at the bottom
        const groundY = this.height - 10;
        
        // Add ground blocks - 10 pixel strip at bottom
        for (let x = 0; x < this.width; x += this.gridSize) {
            this.addStructure(x, groundY, this.gridSize, 10, '#8FBC8F', 'ground');
        }
        
        // Simplified scene for mobile devices
        if (this.isMobile) {
            // Mobile: Simple scene with 2 trees, 1 house, and only high-altitude CIRRUS clouds
            // PHASE 1: 2 background trees
            const numBackgroundTrees = 2;
            for (let i = 0; i < numBackgroundTrees; i++) {
                const treeX = Math.floor((this.width / (numBackgroundTrees + 1)) * (i + 1) / this.gridSize) * this.gridSize;
                const baseHeight = Math.floor((Math.random() * 40 + 50) / this.gridSize) * this.gridSize; // 50-90 pixels
                const heightVariation = Math.random() * 0.3 + 0.85; // 85% to 115% of base height
                const adjustedHeight = Math.floor(baseHeight * heightVariation / this.gridSize) * this.gridSize;
                
                this.addChristmasTree(treeX, groundY - adjustedHeight, adjustedHeight);
            }
            
            // PHASE 2: 1 house in the center
            const houseX = Math.floor((this.width / 2 - 40) / this.gridSize) * this.gridSize;
            const houseWidth = Math.floor(80 / this.gridSize) * this.gridSize; // Fixed 80 pixels
            const houseHeight = Math.floor(70 / this.gridSize) * this.gridSize; // Fixed 70 pixels
            const houseColor = '#CD853F';
            
            this.addComplexStructure(houseX, groundY - houseHeight, houseWidth, houseHeight, houseColor, 'house');
            
            // PHASE 3: Add only high-altitude CIRRUS clouds on mobile
            this.addMobileClouds();
        } else {
            // Desktop: Full scene with clouds, many trees, and multiple houses
            // Add fluffy background clouds in the sky using pixel art
            this.addPixelArtClouds();
            
            // PHASE 1: Draw 30 trees BEHIND everything (background layer) - more trees!
            const numBackgroundTrees = 30;
            for (let i = 0; i < numBackgroundTrees; i++) {
                const treeX = Math.floor(Math.random() * (this.width - 60) / this.gridSize) * this.gridSize;
                // More varied tree heights including smaller trees
                const baseHeight = Math.floor((Math.random() * 60 + 40) / this.gridSize) * this.gridSize; // 40-100 pixels (was 80-120)
                const heightVariation = Math.random() * 0.6 + 0.7; // 70% to 130% of base height
                const adjustedHeight = Math.floor(baseHeight * heightVariation / this.gridSize) * this.gridSize;
                
                this.addChristmasTree(treeX, groundY - adjustedHeight, adjustedHeight);
            }
            
            // PHASE 2: Randomly spawn houses across the scene
            const numHouses = Math.floor(Math.random() * 3) + 2; // 2-4 houses
            for (let i = 0; i < numHouses; i++) {
                const houseX = Math.floor(Math.random() * (this.width - 100) / this.gridSize) * this.gridSize;
                const houseWidth = Math.floor((Math.random() * 40 + 40) / this.gridSize) * this.gridSize; // 40-80 pixels
                const houseHeight = Math.floor((Math.random() * 40 + 60) / this.gridSize) * this.gridSize; // 60-100 pixels
                const houseColor = ['#CD853F', '#DEB887', '#D2691E', '#8B4513'][Math.floor(Math.random() * 4)];
                
                this.addComplexStructure(houseX, groundY - houseHeight, houseWidth, houseHeight, houseColor, 'house');
            }
            
            // PHASE 3: Draw 3-5 foreground trees that can overlap houses - more foreground trees!
            const numForegroundTrees = Math.floor(Math.random() * 3) + 3; // 3-5 trees (was 1-2)
            for (let i = 0; i < numForegroundTrees; i++) {
                const treeX = Math.floor(Math.random() * (this.width - 60) / this.gridSize) * this.gridSize;
                // Foreground trees can be any size including small ones
                const baseHeight = Math.floor((Math.random() * 80 + 30) / this.gridSize) * this.gridSize; // 30-110 pixels (was 80-120)
                const heightVariation = Math.random() * 0.4 + 0.9; // 90% to 130% of base height
                const adjustedHeight = Math.floor(baseHeight * heightVariation / this.gridSize) * this.gridSize;
                
                this.addChristmasTree(treeX, groundY - adjustedHeight, adjustedHeight);
            }
        }
    }

    // Add a structure directly to the grid
    addStructure(x, y, width, height, color, type = 'block') {
        const startGridX = Math.floor(x / this.gridSize);
        const endGridX = Math.floor((x + width - 1) / this.gridSize);
        const startGridY = Math.floor(y / this.gridSize);
        const endGridY = Math.floor((y + height - 1) / this.gridSize);
        
        for (let gy = startGridY; gy <= endGridY; gy++) {
            for (let gx = startGridX; gx <= endGridX; gx++) {
                if (gy >= 0 && gy < this.grid.length && gx >= 0 && gx < this.grid[0].length) {
                    this.grid[gy][gx] = new GridCell('structure', {
                        type: type,
                        color: color,
                        originalX: x,
                        originalY: y,
                        originalWidth: width,
                        originalHeight: height,
                        gridX: gx,
                        gridY: gy
                    });
                }
            }
        }
    }

    // Add a complex structure with multiple parts (like a house)
    addComplexStructure(x, y, width, height, color, type = 'house') {
        // Simple roof choice: triangle or flat
        const hasTriangleRoof = Math.random() < 0.7; // 70% triangle, 30% flat
        
        // Additional roof style variety
        const roofStyle = Math.random();
        const isGabled = roofStyle < 0.3; // 30% gabled roofs
        const isSteep = roofStyle < 0.5; // 50% steep roofs
        
        // Make house wider if it has windows to accommodate them properly
        const hasWindows = Math.random() < 0.7; // 70% chance of windows
        const houseWidth = hasWindows ? width * 1.4 : width; // 40% wider if windows
        
        // Calculate roof height first
        const roofHeight = hasTriangleRoof ? Math.floor(height * (isSteep ? 0.5 : 0.4) / this.gridSize) * this.gridSize : 0;
        
        // House body starts below the roof
        const houseBodyStartY = y + roofHeight;
        const houseBodyHeight = height - roofHeight;
        
        // Grid coordinates for house body
        const houseStartGridX = Math.floor(x / this.gridSize);
        const houseEndGridX = Math.floor((x + houseWidth) / this.gridSize);
        const houseStartGridY = Math.floor(houseBodyStartY / this.gridSize);
        const houseEndGridY = Math.floor((y + height) / this.gridSize);
        
        // Draw house body (walls)
        for (let gy = houseStartGridY; gy < houseEndGridY; gy++) {
            for (let gx = houseStartGridX; gx < houseEndGridX; gx++) {
                if (gy >= 0 && gy < this.grid.length && gx >= 0 && gx < this.grid[0].length) {
                    this.grid[gy][gx] = new GridCell('structure', {
                        type: 'house',
                        color: color,
                        gridX: gx,
                        gridY: gy
                    });
                }
            }
        }
        
        if (hasTriangleRoof) {
            // Proper triangle roof - starts narrow at top, gets wider toward bottom
            const roofY = y; // Roof starts at the very top
            const roofHeight = Math.floor(height * (isSteep ? 0.5 : 0.4) / this.gridSize) * this.gridSize;
            
            // Roof colors - more realistic and varied
            const roofColors = ['#8B4513', '#654321', '#8B7355', '#A0522D', '#CD853F', '#696969', '#708090'];
            const roofColor = roofColors[Math.floor(Math.random() * roofColors.length)];
            
            if (isGabled) {
                // Gabled roof - more realistic peaked roof with proper triangle shape
                for (let gy = Math.floor(roofY / this.gridSize); gy < Math.floor((roofY + roofHeight) / this.gridSize); gy++) {
                    const rowHeight = gy - Math.floor(roofY / this.gridSize);
                    // Create triangle: narrow at top, wider at bottom, but bottom is at least 1.5x house width
                    const progress = rowHeight / (roofHeight / this.gridSize); // 0 at top, 1 at bottom
                    const roofWidth = Math.floor((houseWidth * 0.2 + progress * houseWidth * 1.3) / this.gridSize) * this.gridSize;
                    
                    if (roofWidth > 0) {
                        const startX = x + Math.floor((houseWidth - roofWidth) / 2);
                        const endX = startX + roofWidth;
                        const startGridX = Math.floor(startX / this.gridSize);
                        const endGridX = Math.floor((endX - 1) / this.gridSize);
                        
                        for (let gx = startGridX; gx <= endGridX; gx++) {
                            if (gy >= 0 && gy < this.grid.length && gx >= 0 && gx < this.grid[0].length) {
                                this.grid[gy][gx] = new GridCell('structure', {
                                    type: 'roof',
                                    color: roofColor,
                                    gridX: gx,
                                    gridY: gy
                                });
                            }
                        }
                    }
                }
            } else {
                // Standard triangle roof - proper triangle shape with overhangs
                for (let gy = Math.floor(roofY / this.gridSize); gy < Math.floor((roofY + roofHeight) / this.gridSize); gy++) {
                    const rowHeight = gy - Math.floor(roofY / this.gridSize);
                    // Create triangle: narrow at top, wider at bottom, but bottom is at least 1.4x house width
                    const progress = rowHeight / (roofHeight / this.gridSize); // 0 at top, 1 at bottom
                    const roofWidth = Math.floor((houseWidth * 0.1 + progress * houseWidth * 1.3) / this.gridSize) * this.gridSize;
                    
                    if (roofWidth > 0) {
                        const startX = x + Math.floor((houseWidth - roofWidth) / 2);
                        const endX = startX + roofWidth;
                        const startGridX = Math.floor(startX / this.gridSize);
                        const endGridX = Math.floor((endX - 1) / this.gridSize);
                        
                        for (let gx = startGridX; gx <= endGridX; gx++) {
                            if (gy >= 0 && gy < this.grid.length && gx >= 0 && gx < this.grid[0].length) {
                                this.grid[gy][gx] = new GridCell('structure', {
                                    type: 'roof',
                                    color: roofColor,
                                    gridX: gx,
                                    gridY: gy
                                });
                            }
                        }
                    }
                }
            }
        } else {
            // Flat roof
            const roofGridY = Math.floor(y / this.gridSize);
            for (let gx = houseStartGridX; gx < houseEndGridX; gx++) {
                if (roofGridY >= 0 && roofGridY < this.grid.length && gx >= 0 && gx < this.grid[0].length) {
                    this.grid[roofGridY][gx] = new GridCell('structure', {
                        type: 'roof',
                        color: '#696969', // Grey roof
                        gridX: gx,
                        gridY: roofGridY
                    });
                }
            }
        }
        
        // Simple door (centered, properly sized relative to house)
        const doorWidth = Math.floor(houseWidth * 0.25); // 25% of house width
        const doorHeight = Math.floor(houseBodyHeight * 0.4); // 40% of house body height
        const doorX = x + Math.floor((houseWidth - doorWidth) / 2);
        const doorY = houseBodyStartY + houseBodyHeight - doorHeight; // Position relative to house body
        
        const doorStartGridX = Math.floor(doorX / this.gridSize);
        const doorEndGridX = Math.floor((doorX + doorWidth - 1) / this.gridSize);
        const doorStartGridY = Math.floor(doorY / this.gridSize);
        const doorEndGridY = Math.floor((doorY + doorHeight - 1) / this.gridSize);
        
        for (let gy = doorStartGridY; gy <= doorEndGridY; gy++) {
            for (let gx = doorStartGridX; gx <= doorEndGridX; gx++) {
                if (gy >= 0 && gy < this.grid.length && gx >= 0 && gx < this.grid[0].length) {
                    this.grid[gy][gx] = new GridCell('structure', {
                        type: 'door',
                        color: '#654321', // Dark brown door
                        gridX: gx,
                        gridY: gy
                    });
                }
            }
        }
        
        // Windows (one on each side of door, properly sized) - only if house has windows
        if (hasWindows) {
            const windowSize = Math.floor(houseWidth * 0.15); // 15% of house width
            const windowY = houseBodyStartY + Math.floor(houseBodyHeight * 0.25); // Positioned higher up on house body
            
            // Left window
            const leftWindowX = doorX - windowSize - Math.floor(houseWidth * 0.1);
            const leftWindowStartGridX = Math.floor(leftWindowX / this.gridSize);
            const leftWindowEndGridX = Math.floor((leftWindowX + windowSize - 1) / this.gridSize);
            const leftWindowStartGridY = Math.floor(windowY / this.gridSize);
            const leftWindowEndGridY = Math.floor((windowY + windowSize - 1) / this.gridSize);
            
            for (let gy = leftWindowStartGridY; gy <= leftWindowEndGridY; gy++) {
                for (let gx = leftWindowStartGridX; gx <= leftWindowEndGridX; gx++) {
                    if (gy >= 0 && gy < this.grid.length && gx >= 0 && gx < this.grid[0].length) {
                        this.grid[gy][gx] = new GridCell('structure', {
                            type: 'window',
                            color: '#87CEEB', // Light blue windows
                            gridX: gx,
                            gridY: gy
                        });
                    }
                }
            }
            
            // Right window
            const rightWindowX = doorX + doorWidth + Math.floor(houseWidth * 0.1);
            const rightWindowStartGridX = Math.floor(rightWindowX / this.gridSize);
            const rightWindowEndGridX = Math.floor((rightWindowX + windowSize - 1) / this.gridSize);
            const rightWindowStartGridY = Math.floor(windowY / this.gridSize);
            const rightWindowEndGridY = Math.floor((windowY + windowSize - 1) / this.gridSize);
            
            for (let gy = rightWindowStartGridY; gy <= rightWindowEndGridY; gy++) {
                for (let gx = rightWindowStartGridX; gx <= rightWindowEndGridX; gx++) {
                    if (gy >= 0 && gy < this.grid.length && gx >= 0 && gx < this.grid[0].length) {
                        this.grid[gy][gx] = new GridCell('structure', {
                            type: 'window',
                            color: '#87CEEB', // Light blue windows
                            gridX: gx,
                            gridY: gy
                        });
                    }
                }
            }
        }
        
        // Add chimney to some houses (30% chance)
        if (hasTriangleRoof && Math.random() < 0.3) {
            this.addChimney(x, y, houseWidth, height, roofHeight);
        }
    }
    
    // Add a chimney to a house
    addChimney(houseX, houseY, houseWidth, houseHeight, roofHeight) {
        const chimneyWidth = Math.floor(this.gridSize * 1.5);
        const chimneyHeight = Math.floor(this.gridSize * 5); // Made taller
        
        // Position chimney further from center, biased toward the edges
        const minX = houseX + Math.floor(houseWidth * 0.15); // Closer to left edge
        const maxX = houseX + houseWidth - chimneyWidth - Math.floor(houseWidth * 0.15); // Closer to right edge
        
        // Ensure chimney can actually fit and position is valid
        let chimneyX;
        if (maxX > minX) {
            // Bias positioning toward edges rather than center
            const centerX = houseX + houseWidth / 2;
            const leftSide = minX + Math.floor((centerX - minX) * 0.3); // 30% into left side
            const rightSide = centerX + Math.floor((maxX - centerX) * 0.7); // 70% into right side
            
            // 70% chance to place on left side, 30% chance on right side
            if (Math.random() < 0.7) {
                chimneyX = minX + Math.floor(Math.random() * (leftSide - minX));
            } else {
                chimneyX = rightSide + Math.floor(Math.random() * (maxX - rightSide));
            }
        } else {
            // If chimney is too wide for house, center it
            chimneyX = houseX + Math.floor((houseWidth - chimneyWidth) / 2);
        }
        
        // Simple: chimney bottom is 1 pixel above the roof bottom
        const roofBottomY = houseY + roofHeight;
        const chimneyY = roofBottomY - chimneyHeight - 1 - 10; // Move all chimneys up 10 pixels (subtract Y to go up)
        
        
        const chimneyStartGridX = Math.floor(chimneyX / this.gridSize);
        const chimneyEndGridX = Math.floor((chimneyX + chimneyWidth - 1) / this.gridSize);
        const chimneyStartGridY = Math.floor(chimneyY / this.gridSize);
        const chimneyEndGridY = Math.floor((chimneyY + chimneyHeight - 1) / this.gridSize);
        
        // Chimney colors - realistic grey/black tones
        const chimneyColors = ['#696969', '#708090', '#2F4F4F', '#363636', '#404040', '#555555'];
        const chimneyColor = chimneyColors[Math.floor(Math.random() * chimneyColors.length)];
        
        for (let gy = chimneyStartGridY; gy <= chimneyEndGridY; gy++) {
            for (let gx = chimneyStartGridX; gx <= chimneyEndGridX; gx++) {
                if (gy >= 0 && gy < this.grid.length && gx >= 0 && gx < this.grid[0].length) {
                    this.grid[gy][gx] = new GridCell('structure', {
                        type: 'chimney',
                        color: chimneyColor,
                        gridX: gx,
                        gridY: gy
                    });
                }
            }
        }
    }

    // Add a Christmas tree structure
    addChristmasTree(x, y, height) {
        const treeWidth = this.gridSize; // Single grid cell width for trunk
        const trunkHeight = Math.floor(height * 0.2 / this.gridSize) * this.gridSize;
        
        // Randomly choose tree style (some wider, some normal)
        const treeStyles = ['normal', 'wide', 'very-wide'];
        const treeStyle = treeStyles[Math.floor(Math.random() * treeStyles.length)];
        
        // Add trunk
        this.addStructure(x, y + height - trunkHeight, treeWidth, trunkHeight, '#8B4513', 'tree');
        
        const foliageHeight = height - trunkHeight;
        const foliageStartY = y;
        
        // Calculate foliage width based on style
        let baseWidth;
        switch (treeStyle) {
            case 'normal':
                baseWidth = 2.0; // Normal width
                break;
            case 'wide':
                baseWidth = 3.0; // Wider for snow accumulation
                break;
            case 'very-wide':
                baseWidth = 4.0; // Very wide for lots of snow
                break;
            default:
                baseWidth = 2.0; // Default to normal
                break;
        }
        
        // Choose one tree color for this entire tree (with shading)
        const treeColorVariation = Math.floor(Math.random() * 4); // 0, 1, 2, or 3
        let baseTreeColor;
        switch (treeColorVariation) {
            case 0:
                baseTreeColor = '#228B22'; // Forest green
                break;
            case 1:
                baseTreeColor = '#32CD32'; // Lime green
                break;
            case 2:
                baseTreeColor = '#006400'; // Dark green
                break;
            case 3:
                baseTreeColor = '#228B22'; // Forest green (original)
                break;
            default:
                baseTreeColor = '#228B22'; // Default to forest green
                break;
        }
        
        // Add foliage with varying width
        for (let gy = Math.floor(foliageStartY / this.gridSize); gy < Math.floor((foliageStartY + foliageHeight) / this.gridSize); gy++) {
            const rowHeight = gy - Math.floor(foliageStartY / this.gridSize);
            const maxWidth = Math.floor((rowHeight + 1) * baseWidth / this.gridSize) * this.gridSize;
            
            if (maxWidth > 0) {
                const startX = x - Math.floor(maxWidth * 0.5 / this.gridSize) * this.gridSize;
                const endX = x + Math.floor(maxWidth * 0.5 / this.gridSize) * this.gridSize;
                
                for (let gx = Math.floor(startX / this.gridSize); gx <= Math.floor(endX / this.gridSize); gx++) {
                    if (gy >= 0 && gy < this.grid.length && gx >= 0 && gx < this.grid[0].length) {
                        // Add subtle shading based on position in tree (darker at bottom, lighter at top)
                        const relativeY = (gy - Math.floor(foliageStartY / this.gridSize)) / Math.floor(foliageHeight / this.gridSize);
                        let treeColor = baseTreeColor;
                        
                        if (relativeY > 0.8) {
                            // Bottom of tree - very subtle darker shade
                            treeColor = this.adjustColorBrightness(baseTreeColor, -8);
                        } else if (relativeY > 0.6) {
                            // Lower middle - barely darker
                            treeColor = this.adjustColorBrightness(baseTreeColor, -4);
                        } else if (relativeY > 0.4) {
                            // Upper middle - barely lighter
                            treeColor = this.adjustColorBrightness(baseTreeColor, 3);
                        } else {
                            // Top of tree - subtle lighter shade
                            treeColor = this.adjustColorBrightness(baseTreeColor, 6);
                        }
                        
                        this.grid[gy][gx] = new GridCell('structure', {
                            type: 'tree',
                            color: treeColor,
                            gridX: gx,
                            gridY: gy
                        });
                    }
                }
            }
        }
        
        // Christmas lights are winter-only — summer gets plain shade trees
        const festive =
            this.behavior !== 'fireworks' &&
            this.theme?.id !== 'summer' &&
            this.theme?.id !== 'spring' &&
            this.theme?.id !== 'fall';
        if (festive && Math.random() < 0.25) {
            this.addChristmasLights(x, y, height, treeStyle);
        }
    }
    
    addChristmasLights(x, y, height, treeStyle) {
        const trunkHeight = Math.floor(height * 0.2 / this.gridSize) * this.gridSize;
        const foliageHeight = height - trunkHeight;
        const foliageStartY = y;
        
        // Calculate base width for this tree style
        let baseWidth;
        switch (treeStyle) {
            case 'normal':
                baseWidth = 2.0;
                break;
            case 'wide':
                baseWidth = 3.0;
                break;
            case 'very-wide':
                baseWidth = 4.0;
                break;
            default:
                baseWidth = 2.0; // Default to normal
                break;
        }
        
        // Add lights in a spiral pattern around the tree (better spacing and visibility)
        const lightColors = ['#FF0000', '#00FF00', '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500', '#FF69B4', '#32CD32'];
        let lightIndex = 0;
        
        for (let gy = Math.floor(foliageStartY / this.gridSize); gy < Math.floor((foliageStartY + foliageHeight) / this.gridSize); gy++) {
            const rowHeight = gy - Math.floor(foliageStartY / this.gridSize);
            const maxWidth = Math.floor((rowHeight + 1) * baseWidth / this.gridSize) * this.gridSize;
            
            if (maxWidth > 0) {
                const startX = x - Math.floor(maxWidth * 0.5 / this.gridSize) * this.gridSize;
                const endX = x + Math.floor(maxWidth * 0.5 / this.gridSize) * this.gridSize;
                
                // Add lights every 4th cell for better spacing and increase probability
                for (let gx = Math.floor(startX / this.gridSize); gx <= Math.floor(endX / this.gridSize); gx++) {
                    if (gy >= 0 && gy < this.grid.length && gx >= 0 && gx < this.grid[0].length) {
                        if ((gx + gy) % 4 === 0 && Math.random() < 0.6) { // Better spacing and higher probability
                            const lightColor = lightColors[lightIndex % lightColors.length];
                            this.grid[gy][gx] = new GridCell('structure', {
                                type: 'christmas-light',
                                color: lightColor,
                                isLight: true,
                                lightColor: lightColor,
                                gridX: gx,
                                gridY: gy
                            });
                            lightIndex++;
                        }
                    }
                }
            }
        }
    }

    // Draw wind vectors visualization
    drawWindVectors() {
        if (!this.showWindArrows || !this.windGrid) return;
        
        this.ctx.strokeStyle = '#00FF00';
        this.ctx.lineWidth = 2; // Thicker lines for better visibility
        this.ctx.globalAlpha = 0.8; // More opaque for better visibility
        
        // Draw wind arrows every 3rd grid cell (more frequent for better coverage)
        for (let y = 0; y < this.windGrid.length; y += 3) {
            for (let x = 0; x < this.windGrid[0].length; x += 3) {
                const cell = this.windGrid[y][x];
                const windVector = cell.windVector;
                
                // Only draw arrows for significant wind (lower threshold for more arrows)
                if (Math.abs(windVector.x) > 0.05 || Math.abs(windVector.y) > 0.05) {
                    const centerX = x * this.gridSize + this.gridSize / 2;
                    const centerY = y * this.gridSize + this.gridSize / 2;
                    const endX = centerX + windVector.x * 30; // Longer arrows for better visibility
                    const endY = centerY + windVector.y * 30;
                    
                    // Draw main arrow line
                    this.ctx.beginPath();
                    this.ctx.moveTo(centerX, centerY);
                    this.ctx.lineTo(endX, endY);
                    this.ctx.stroke();
                    
                    // Draw arrow head
                    const angle = Math.atan2(endY - centerY, endX - centerX);
                    const arrowLength = 10; // Bigger arrow heads
                    
                    this.ctx.beginPath();
                    this.ctx.moveTo(endX, endY);
                    this.ctx.lineTo(
                        endX - arrowLength * Math.cos(angle - Math.PI/6),
                        endY - arrowLength * Math.sin(angle - Math.PI/6)
                    );
                    this.ctx.moveTo(endX, endY);
                    this.ctx.lineTo(
                        endX - arrowLength * Math.cos(angle + Math.PI/6),
                        endY - arrowLength * Math.sin(angle + Math.PI/6)
                    );
                    this.ctx.stroke();
                }
            }
        }
        
        this.ctx.globalAlpha = 1.0;
    }

    // Update grid to include structures and particles
    updateGrid() {
        // Clear grid
        this.grid = this.createGrid();
        
        // Re-add structures (this will be handled by addStructure calls)
        this.createDefaultStructures();
        
        // Re-add particles to grid
        for (const particle of this.particles) {
            const gridX = Math.floor(particle.x / this.gridSize);
            const gridY = Math.floor(particle.y / this.gridSize);
            if (gridY >= 0 && gridY < this.grid.length && gridX >= 0 && gridX < this.grid[0].length) {
                this.grid[gridY][gridX] = new GridCell('particle', { particle: particle });
            }
        }
        

    }

    setupEventListeners() {
        this.canvas.addEventListener('mousedown', (e) => {
            const rect = this.canvas.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            this.currentSpawnX = x;
            this.currentSpawnY = y;
            this.startSpawning(x, y);
        });

        this.canvas.addEventListener('mousemove', (e) => {
            if (this.manualSpawnInterval) {
                const rect = this.canvas.getBoundingClientRect();
                this.currentSpawnX = e.clientX - rect.left;
                this.currentSpawnY = e.clientY - rect.top;
            }
        });

        this.canvas.addEventListener('mouseup', () => {
            this.stopSpawning();
        });

        this.canvas.addEventListener('mouseleave', () => {
            this.stopSpawning();
        });

        const clearBtnInSetup = document.getElementById('clearBtn');
        if (clearBtnInSetup) {
            clearBtnInSetup.addEventListener('click', () => {
                this.clearAll();
            });
        }


    }

    setupControls() {
        // Clear button
        const clearBtn = document.getElementById('clearBtn');
        if (clearBtn) {
            clearBtn.addEventListener('click', () => this.clearAll());
        }
        
        // Rain/Snow button
        const rainBtn = document.getElementById('rainBtn');
        if (rainBtn) {
            rainBtn.addEventListener('click', () => this.toggleSnow());
        }

        // Manual fireworks finale (summer weather machine)
        const finaleBtn = document.getElementById('finaleBtn');
        if (finaleBtn) {
            finaleBtn.addEventListener('click', () => this.toggleFireworkFinale());
        }

        // Mortar console: toggle battery / count, fire an effect type from a battery
        const fwBatteryRow = document.getElementById('fwBatteryRow');
        if (fwBatteryRow) {
            fwBatteryRow.addEventListener('click', (e) => {
                const btn = e.target.closest('[data-fw-battery]');
                if (!btn) return;
                fwBatteryRow.querySelectorAll('[data-fw-battery]').forEach((b) => {
                    b.classList.toggle('active', b === btn);
                });
            });
        }
        const fwCountRow = document.getElementById('fwCountRow');
        if (fwCountRow) {
            fwCountRow.addEventListener('click', (e) => {
                const btn = e.target.closest('[data-fw-count]');
                if (!btn) return;
                fwCountRow.querySelectorAll('[data-fw-count]').forEach((b) => {
                    b.classList.toggle('active', b === btn);
                });
            });
        }
        const fwCaliberRow = document.getElementById('fwCaliberRow');
        if (fwCaliberRow) {
            fwCaliberRow.addEventListener('click', (e) => {
                const btn = e.target.closest('[data-fw-caliber]');
                if (!btn) return;
                fwCaliberRow.querySelectorAll('[data-fw-caliber]').forEach((b) => {
                    b.classList.toggle('active', b === btn);
                });
            });
        }
        const fwEffectGrid = document.getElementById('fwEffectGrid');
        if (fwEffectGrid) {
            fwEffectGrid.addEventListener('click', (e) => {
                const effectBtn = e.target.closest('[data-fw-effect]');
                if (!effectBtn) return;

                const battery =
                    document.querySelector('#fwBatteryRow [data-fw-battery].active')
                        ?.getAttribute('data-fw-battery') || 'center';
                const count = parseInt(
                    document.querySelector('#fwCountRow [data-fw-count].active')
                        ?.getAttribute('data-fw-count') || '6',
                    10
                );
                const caliberId =
                    document.querySelector('#fwCaliberRow [data-fw-caliber].active')
                        ?.getAttribute('data-fw-caliber') || 'five';

                this.fireShellsFromBattery({
                    battery,
                    effectId: effectBtn.getAttribute('data-fw-effect'),
                    count,
                    caliberId,
                });
            });
        }

        const fwPatternGrid = document.getElementById('fwPatternGrid');
        if (fwPatternGrid) {
            fwPatternGrid.addEventListener('click', (e) => {
                const patternBtn = e.target.closest('[data-fw-pattern]');
                if (!patternBtn) return;
                const count = parseInt(
                    document.querySelector('#fwCountRow [data-fw-count].active')
                        ?.getAttribute('data-fw-count') || '6',
                    10
                );
                const caliberId =
                    document.querySelector('#fwCaliberRow [data-fw-caliber].active')
                        ?.getAttribute('data-fw-caliber') || 'five';
                this.fireManualPattern({
                    pattern: patternBtn.getAttribute('data-fw-pattern'),
                    count,
                    caliberId,
                });
            });
        }
        

        
        // Snow per click slider (optional - in stats section)
        const countSlider = document.getElementById('countSlider');
        const countValue = document.getElementById('countValue');
        if (countSlider && countValue) {
            countSlider.addEventListener('input', (e) => {
                this.particlesPerClick = parseInt(e.target.value);
                countValue.textContent = e.target.value;
            });
        }
        
        // Flakes per minute slider
        const flakesPerMinuteSlider = document.getElementById('flakesPerMinuteSlider');
        const flakesPerMinuteValue = document.getElementById('flakesPerMinuteValue');
        if (flakesPerMinuteSlider && flakesPerMinuteValue) {
            flakesPerMinuteSlider.addEventListener('input', (e) => {
            const flakesPerMinute = parseInt(e.target.value);
            flakesPerMinuteValue.textContent = flakesPerMinute;
            
            // Update base snowfall rate if storm variance is enabled
            if (this.stormVarianceEnabled) {
                this.baseFlakesPerMinute = flakesPerMinute;
                // Reset variance targets to follow the new base value
                this.flakesPerMinute = flakesPerMinute;
                this.snowfallVarianceTarget = flakesPerMinute;
                this.currentSpawnRate = flakesPerMinute;
            }
            
            // Update spawn rate if snow is currently falling
            if (this.snowMode && this.spawnInterval) {
                this.updateSnowfallRate(flakesPerMinute);
            }
            
            // Update the display
            this.updateSnowfallDisplay(flakesPerMinute);
            
            // Update estimated flakes per minute stat
            this.updateEstimatedFlakesStat();
            });
        }
        
        // Smallest pieces slider
        const smallestPiecesSlider = document.getElementById('smallestPiecesSlider');
        const smallestPiecesValue = document.getElementById('smallestPiecesValue');
        if (smallestPiecesSlider && smallestPiecesValue) {
            smallestPiecesSlider.addEventListener('input', (e) => {
            const newSize = parseInt(e.target.value);
            smallestPiecesValue.textContent = newSize === 1 ? '1 piece' : `${newSize} pieces`;
            this.smallestPieces = newSize;
            
            // Ensure biggest pieces is never less than smallest pieces
            if (this.biggestPieces < this.smallestPieces) {
                this.biggestPieces = this.smallestPieces;
                const biggestPiecesSlider = document.getElementById('biggestPiecesSlider');
                const biggestPiecesValue = document.getElementById('biggestPiecesValue');
                if (biggestPiecesSlider) biggestPiecesSlider.value = this.biggestPieces;
                if (biggestPiecesValue) biggestPiecesValue.textContent = this.biggestPieces === 1 ? '1 piece' : `${this.biggestPieces} pieces`;
            }
            
            // Update estimated flakes per minute stat
            this.updateEstimatedFlakesStat();
            });
        }
        
        // Biggest pieces slider
        const biggestPiecesSlider = document.getElementById('biggestPiecesSlider');
        const biggestPiecesValue = document.getElementById('biggestPiecesValue');
        if (biggestPiecesSlider && biggestPiecesValue) {
            biggestPiecesSlider.addEventListener('input', (e) => {
            const newSize = parseInt(e.target.value);
            biggestPiecesValue.textContent = newSize === 1 ? '1 piece' : `${newSize} pieces`;
            this.biggestPieces = newSize;
            
            // Ensure smallest pieces is never more than biggest pieces
            if (this.smallestPieces > this.biggestPieces) {
                this.smallestPieces = this.biggestPieces;
                const smallestPiecesSlider = document.getElementById('smallestPiecesSlider');
                const smallestPiecesValue = document.getElementById('smallestPiecesValue');
                if (smallestPiecesSlider) smallestPiecesSlider.value = this.smallestPieces;
                if (smallestPiecesValue) smallestPiecesValue.textContent = this.smallestPieces === 1 ? '1 piece' : `${this.smallestPieces} pieces`;
            }
            
            // Update estimated flakes per minute stat
            this.updateEstimatedFlakesStat();
            });
        }
        
        // Min fall speed slider
        const minFallSpeedSlider = document.getElementById('minFallSpeedSlider');
        const minFallSpeedValue = document.getElementById('minFallSpeedValue');
        if (minFallSpeedSlider && minFallSpeedValue) {
            minFallSpeedSlider.addEventListener('input', (e) => {
            const newSpeed = parseFloat(e.target.value);
            minFallSpeedValue.textContent = newSpeed.toFixed(1);
            this.minFallSpeed = newSpeed;
            
            // Ensure max fall speed is never less than min fall speed
            if (this.maxFallSpeed < this.minFallSpeed) {
                this.maxFallSpeed = this.minFallSpeed;
                const maxFallSpeedSlider = document.getElementById('maxFallSpeedSlider');
                const maxFallSpeedValue = document.getElementById('maxFallSpeedValue');
                if (maxFallSpeedSlider) maxFallSpeedSlider.value = this.maxFallSpeed;
                if (maxFallSpeedValue) maxFallSpeedValue.textContent = this.maxFallSpeed.toFixed(1);
            }
            });
        }
        
        // Max fall speed slider
        const maxFallSpeedSlider = document.getElementById('maxFallSpeedSlider');
        const maxFallSpeedValue = document.getElementById('maxFallSpeedValue');
        if (maxFallSpeedSlider && maxFallSpeedValue) {
            maxFallSpeedSlider.addEventListener('input', (e) => {
            const newSpeed = parseFloat(e.target.value);
            maxFallSpeedValue.textContent = newSpeed.toFixed(1);
            this.maxFallSpeed = newSpeed;
            
            // Ensure min fall speed is never more than max fall speed
            if (this.minFallSpeed > this.maxFallSpeed) {
                this.minFallSpeed = this.maxFallSpeed;
                const minFallSpeedSlider = document.getElementById('minFallSpeedSlider');
                const minFallSpeedValue = document.getElementById('minFallSpeedValue');
                if (minFallSpeedSlider) minFallSpeedSlider.value = this.minFallSpeed;
                if (minFallSpeedValue) minFallSpeedValue.textContent = this.minFallSpeed.toFixed(1);
            }
            });
        }
        
        // Wind slider
        const windSlider = document.getElementById('windSlider');
        const windValue = document.getElementById('windValue');
        
        if (windSlider && windValue) {
            // Set default wind value from constructor
            windSlider.value = this.windStrength;
            
            // Display initial base wind value (the setting)
            const windBaseValue = document.getElementById('windBaseValue');
            if (windBaseValue) {
                if (this.windStrength > 0) {
                    const displayValue = Number.isInteger(this.windStrength) ? this.windStrength : this.windStrength.toFixed(1);
                    windBaseValue.textContent = `${displayValue}`;
                } else if (this.windStrength < 0) {
                    const displayValue = Number.isInteger(this.windStrength) ? Math.abs(this.windStrength) : Math.abs(this.windStrength).toFixed(1);
                    windBaseValue.textContent = `-${displayValue}`;
                } else {
                    windBaseValue.textContent = '0';
                }
            }
            
            // Display initial wind with directional arrows and decimal precision (current value)
            if (this.windStrength > 0) {
                const displayValue = Number.isInteger(this.windStrength) ? this.windStrength : this.windStrength.toFixed(1);
                windValue.textContent = `${displayValue} >`;
            } else if (this.windStrength < 0) {
                const displayValue = Number.isInteger(this.windStrength) ? Math.abs(this.windStrength) : Math.abs(this.windStrength).toFixed(1);
                windValue.textContent = `< ${displayValue}`;
            } else {
                windValue.textContent = '0';
            }
            
            // Initialize wind variance display
            this.updateWindDisplay(this.windStrength);
            
            // Set initial values for other sliders (with null checks)
            if (smallestPiecesSlider) smallestPiecesSlider.value = this.smallestPieces;
            if (biggestPiecesSlider) biggestPiecesSlider.value = this.biggestPieces;
            if (minFallSpeedSlider) minFallSpeedSlider.value = this.minFallSpeed;
            if (maxFallSpeedSlider) maxFallSpeedSlider.value = this.maxFallSpeed;
            
            // Update display values (with null checks)
            if (smallestPiecesValue) smallestPiecesValue.textContent = this.smallestPieces === 1 ? '1 piece' : `${this.smallestPieces} pieces`;
            if (biggestPiecesValue) biggestPiecesValue.textContent = this.biggestPieces === 1 ? '1 piece' : `${this.biggestPieces} pieces`;
            if (minFallSpeedValue) minFallSpeedValue.textContent = this.minFallSpeed.toFixed(1);
            if (maxFallSpeedValue) maxFallSpeedValue.textContent = this.maxFallSpeed.toFixed(1);
            
            windSlider.addEventListener('input', (e) => {
                this.windStrength = parseFloat(e.target.value);
                
                // Always update base wind strength (this is the user's setting)
                this.baseWindStrength = this.windStrength;
                
                // Reset variance targets to follow the new base value
                if (this.stormVarianceEnabled) {
                    this.windVarianceTarget = this.windStrength;
                }
                
                // Update base wind display (the setting value)
                const windBaseValue = document.getElementById('windBaseValue');
                if (windBaseValue) {
                    if (this.windStrength > 0) {
                        const displayValue = Number.isInteger(this.windStrength) ? this.windStrength : this.windStrength.toFixed(1);
                        windBaseValue.textContent = `${displayValue}`;
                    } else if (this.windStrength < 0) {
                        const displayValue = Number.isInteger(this.windStrength) ? Math.abs(this.windStrength) : Math.abs(this.windStrength).toFixed(1);
                        windBaseValue.textContent = `-${displayValue}`;
                    } else {
                        windBaseValue.textContent = '0';
                    }
                }
                
                // Display wind with directional arrows and decimal precision (current value)
                if (this.windStrength > 0) {
                    const displayValue = Number.isInteger(this.windStrength) ? this.windStrength : this.windStrength.toFixed(1);
                    windValue.textContent = `${displayValue} >`;
                } else if (this.windStrength < 0) {
                    const displayValue = Number.isInteger(this.windStrength) ? Math.abs(this.windStrength) : Math.abs(this.windStrength).toFixed(1);
                    windValue.textContent = `< ${displayValue}`;
                } else {
                    windValue.textContent = '0';
                }
                
                // Only update wind variance display if variance is enabled
                if (this.stormVarianceEnabled) {
                    this.updateWindDisplay(this.windStrength);
                }
                
                // Update wind grid when wind strength changes
                this.updateWindGrid();
            });
        }
        
        // Wind arrows toggle button
        const windArrowsBtn = document.getElementById('windArrowsBtn');
        if (windArrowsBtn) {
            windArrowsBtn.addEventListener('click', () => {
                this.showWindArrows = !this.showWindArrows;
                windArrowsBtn.textContent = this.showWindArrows ? 'Hide Wind Arrows' : 'Show Wind Arrows';
            });
        }

        
        // Variance Panel Controls
        const enableStormVariance = document.getElementById('enableStormVariance');
        const windVarianceSlider = document.getElementById('windVarianceSlider');
        const windVarianceValue = document.getElementById('windVarianceValue');
        const snowfallVarianceSlider = document.getElementById('snowfallVarianceSlider');
        const snowfallVarianceValue = document.getElementById('snowfallVarianceValue');
        const varianceSpeedSlider = document.getElementById('varianceSpeedSlider');
        const varianceSpeedValue = document.getElementById('varianceSpeedValue');
        
        // Set initial values for variance panel
        if (enableStormVariance && windVarianceSlider && windVarianceValue && 
            snowfallVarianceSlider && snowfallVarianceValue && 
            varianceSpeedSlider && varianceSpeedValue) {
            
            enableStormVariance.checked = this.stormVarianceEnabled;
            windVarianceSlider.value = this.windVariance;
            snowfallVarianceSlider.value = this.snowfallVariance;
            varianceSpeedSlider.value = this.varianceSpeed;
            windVarianceValue.textContent = this.windVariance;
            snowfallVarianceValue.textContent = this.snowfallVariance;
            varianceSpeedValue.textContent = this.varianceSpeed;
            
            // Storm variance functionality
            enableStormVariance.addEventListener('change', (e) => {
                this.stormVarianceEnabled = e.target.checked;
            });
            
            // Wind variance slider
            windVarianceSlider.addEventListener('input', (e) => {
                this.windVariance = parseFloat(e.target.value);
                windVarianceValue.textContent = this.windVariance;
            });
            
            // Snowfall variance slider
            snowfallVarianceSlider.addEventListener('input', (e) => {
                this.snowfallVariance = parseInt(e.target.value);
                snowfallVarianceValue.textContent = this.snowfallVariance;
            });
            
            // Variance speed slider
            varianceSpeedSlider.addEventListener('input', (e) => {
                this.varianceSpeed = parseFloat(e.target.value);
                varianceSpeedValue.textContent = this.varianceSpeed;
            });
        }
        
        // Sync HTML sliders with randomized values from constructor
        if (smallestPiecesSlider && smallestPiecesValue) {
            smallestPiecesSlider.value = this.smallestPieces;
            smallestPiecesValue.textContent = this.smallestPieces === 1 ? '1 piece' : `${this.smallestPieces} pieces`;
        }
        
        if (biggestPiecesSlider && biggestPiecesValue) {
            biggestPiecesSlider.value = this.biggestPieces;
            biggestPiecesValue.textContent = this.biggestPieces === 1 ? '1 piece' : `${this.biggestPieces} pieces`;
        }
        
        if (minFallSpeedSlider && minFallSpeedValue) {
            minFallSpeedSlider.value = this.minFallSpeed;
            minFallSpeedValue.textContent = this.minFallSpeed.toFixed(1);
        }
        
        if (maxFallSpeedSlider && maxFallSpeedValue) {
            maxFallSpeedSlider.value = this.maxFallSpeed;
            maxFallSpeedValue.textContent = this.maxFallSpeed.toFixed(1);
        }
        
        // Update flakes per minute slider with randomized value
        if (flakesPerMinuteSlider && flakesPerMinuteValue) {
            flakesPerMinuteSlider.value = this.baseFlakesPerMinute;
            flakesPerMinuteValue.textContent = this.baseFlakesPerMinute;
        }
        
        // Update current flakes per minute display (for variance display)
        const currentFlakesPerMinute = document.getElementById('currentFlakesPerMinute');
        if (currentFlakesPerMinute) {
            currentFlakesPerMinute.textContent = this.flakesPerMinute;
        }
        
        // Cloud controls setup
        this.setupCloudControls();
    }
    
    setupCloudControls() {
        // S1 Cloud controls
        const s1CountSlider = document.getElementById('s1CountSlider');
        const s1CountValue = document.getElementById('s1CountValue');
        const s1MinYSlider = document.getElementById('s1MinYSlider');
        const s1MinYValue = document.getElementById('s1MinYValue');
        const s1MaxYSlider = document.getElementById('s1MaxYSlider');
        const s1MaxYValue = document.getElementById('s1MaxYValue');
        
        // M1 Cloud controls
        const m1CountSlider = document.getElementById('m1CountSlider');
        const m1CountValue = document.getElementById('m1CountValue');
        const m1MinYSlider = document.getElementById('m1MinYSlider');
        const m1MinYValue = document.getElementById('m1MinYValue');
        const m1MaxYSlider = document.getElementById('m1MaxYSlider');
        const m1MaxYValue = document.getElementById('m1MaxYValue');
        
        // Cirrus Cloud controls
        const cirrusCountSlider = document.getElementById('cirrusCountSlider');
        const cirrusCountValue = document.getElementById('cirrusCountValue');
        const cirrusMinYSlider = document.getElementById('cirrusMinYSlider');
        const cirrusMinYValue = document.getElementById('cirrusMinYValue');
        const cirrusMaxYSlider = document.getElementById('cirrusMaxYSlider');
        const cirrusMaxYValue = document.getElementById('cirrusMaxYValue');
        
        // Set initial values (with null checks)
        if (s1CountSlider && s1CountValue && s1MinYSlider && s1MinYValue && s1MaxYSlider && s1MaxYValue) {
            s1CountSlider.value = this.cloudSettings.S1.targetCount;
            s1MinYSlider.value = this.cloudSettings.S1.minY;
            s1MaxYSlider.value = this.cloudSettings.S1.maxY;
            
            s1CountValue.textContent = this.cloudSettings.S1.targetCount;
            s1MinYValue.textContent = this.cloudSettings.S1.minY;
            s1MaxYValue.textContent = this.cloudSettings.S1.maxY;
        }
        
        if (m1CountSlider && m1CountValue && m1MinYSlider && m1MinYValue && m1MaxYSlider && m1MaxYValue) {
            m1CountSlider.value = this.cloudSettings.M1.targetCount;
            m1MinYSlider.value = this.cloudSettings.M1.minY;
            m1MaxYSlider.value = this.cloudSettings.M1.maxY;
            
            m1CountValue.textContent = this.cloudSettings.M1.targetCount;
            m1MinYValue.textContent = this.cloudSettings.M1.minY;
            m1MaxYValue.textContent = this.cloudSettings.M1.maxY;
        }
        
        if (cirrusCountSlider && cirrusCountValue && cirrusMinYSlider && cirrusMinYValue && cirrusMaxYSlider && cirrusMaxYValue) {
            cirrusCountSlider.value = this.cloudSettings.CIRRUS.targetCount;
            cirrusMinYSlider.value = this.cloudSettings.CIRRUS.minY;
            cirrusMaxYSlider.value = this.cloudSettings.CIRRUS.maxY;
            
            cirrusCountValue.textContent = this.cloudSettings.CIRRUS.targetCount;
            cirrusMinYValue.textContent = this.cloudSettings.CIRRUS.minY;
            cirrusMaxYValue.textContent = this.cloudSettings.CIRRUS.maxY;
        }
        
        // S1 event listeners
        if (s1CountSlider && s1CountValue) {
            s1CountSlider.addEventListener('input', (e) => {
                this.cloudSettings.S1.targetCount = parseInt(e.target.value);
                s1CountValue.textContent = this.cloudSettings.S1.targetCount;
                this.manageClouds();
            });
        }
        
        if (s1MinYSlider && s1MinYValue) {
            s1MinYSlider.addEventListener('input', (e) => {
                this.cloudSettings.S1.minY = parseInt(e.target.value);
                s1MinYValue.textContent = this.cloudSettings.S1.minY;
                if (this.cloudSettings.S1.minY > this.cloudSettings.S1.maxY) {
                    this.cloudSettings.S1.maxY = this.cloudSettings.S1.minY;
                    if (s1MaxYSlider) s1MaxYSlider.value = this.cloudSettings.S1.maxY;
                    if (s1MaxYValue) s1MaxYValue.textContent = this.cloudSettings.S1.maxY;
                }
            });
        }
        
        if (s1MaxYSlider && s1MaxYValue) {
            s1MaxYSlider.addEventListener('input', (e) => {
                this.cloudSettings.S1.maxY = parseInt(e.target.value);
                s1MaxYValue.textContent = this.cloudSettings.S1.maxY;
                if (this.cloudSettings.S1.maxY < this.cloudSettings.S1.minY) {
                    this.cloudSettings.S1.minY = this.cloudSettings.S1.maxY;
                    if (s1MinYSlider) s1MinYSlider.value = this.cloudSettings.S1.minY;
                    if (s1MinYValue) s1MinYValue.textContent = this.cloudSettings.S1.minY;
                }
            });
        }
        
        // M1 event listeners
        if (m1CountSlider && m1CountValue) {
            m1CountSlider.addEventListener('input', (e) => {
                this.cloudSettings.M1.targetCount = parseInt(e.target.value);
                m1CountValue.textContent = this.cloudSettings.M1.targetCount;
                this.manageClouds();
            });
        }
        
        if (m1MinYSlider && m1MinYValue) {
            m1MinYSlider.addEventListener('input', (e) => {
                this.cloudSettings.M1.minY = parseInt(e.target.value);
                m1MinYValue.textContent = this.cloudSettings.M1.minY;
                if (this.cloudSettings.M1.minY > this.cloudSettings.M1.maxY) {
                    this.cloudSettings.M1.maxY = this.cloudSettings.M1.minY;
                    if (m1MaxYSlider) m1MaxYSlider.value = this.cloudSettings.M1.maxY;
                    if (m1MaxYValue) m1MaxYValue.textContent = this.cloudSettings.M1.maxY;
                }
            });
        }
        
        if (m1MaxYSlider && m1MaxYValue) {
            m1MaxYSlider.addEventListener('input', (e) => {
                this.cloudSettings.M1.maxY = parseInt(e.target.value);
                m1MaxYValue.textContent = this.cloudSettings.M1.maxY;
                if (this.cloudSettings.M1.maxY < this.cloudSettings.M1.minY) {
                    this.cloudSettings.M1.minY = this.cloudSettings.M1.maxY;
                    if (m1MinYSlider) m1MinYSlider.value = this.cloudSettings.M1.maxY;
                    if (m1MaxYValue) m1MaxYValue.textContent = this.cloudSettings.M1.maxY;
                }
            });
        }
        
        // Cirrus event listeners
        if (cirrusCountSlider && cirrusCountValue) {
            cirrusCountSlider.addEventListener('input', (e) => {
                this.cloudSettings.CIRRUS.targetCount = parseInt(e.target.value);
                cirrusCountValue.textContent = this.cloudSettings.CIRRUS.targetCount;
                this.manageClouds();
            });
        }
        
        if (cirrusMinYSlider && cirrusMinYValue) {
            cirrusMinYSlider.addEventListener('input', (e) => {
                this.cloudSettings.CIRRUS.minY = parseInt(e.target.value);
                cirrusMinYValue.textContent = this.cloudSettings.CIRRUS.minY;
                if (this.cloudSettings.CIRRUS.minY > this.cloudSettings.CIRRUS.maxY) {
                    this.cloudSettings.CIRRUS.maxY = this.cloudSettings.CIRRUS.minY;
                    if (cirrusMaxYSlider) cirrusMaxYSlider.value = this.cloudSettings.CIRRUS.maxY;
                    if (cirrusMaxYValue) cirrusMaxYValue.textContent = this.cloudSettings.CIRRUS.maxY;
                }
            });
        }
        
        if (cirrusMaxYSlider && cirrusMaxYValue) {
            cirrusMaxYSlider.addEventListener('input', (e) => {
                this.cloudSettings.CIRRUS.maxY = parseInt(e.target.value);
                cirrusMaxYValue.textContent = this.cloudSettings.CIRRUS.maxY;
                if (this.cloudSettings.CIRRUS.maxY < this.cloudSettings.CIRRUS.minY) {
                    this.cloudSettings.CIRRUS.minY = this.cloudSettings.CIRRUS.maxY;
                    if (cirrusMinYSlider) cirrusMinYSlider.value = this.cloudSettings.CIRRUS.minY;
                    if (cirrusMinYValue) cirrusMinYValue.textContent = this.cloudSettings.CIRRUS.minY;
                }
            });
        }
        
        // Cloud action buttons
        const regenerateCloudsBtn = document.getElementById('regenerateCloudsBtn');
        const clearCloudsBtn = document.getElementById('clearCloudsBtn');
        
        if (regenerateCloudsBtn) {
            regenerateCloudsBtn.addEventListener('click', () => {
                this.regenerateClouds();
            });
        }
        
        if (clearCloudsBtn) {
            clearCloudsBtn.addEventListener('click', () => {
                this.clearAllClouds();
            });
        }
    }
    
    manageClouds() {
        // Count current clouds of each type
        this.updateCloudCounts();
        
        // Spawn new clouds if needed
        this.spawnMissingClouds();
        
        // Remove clouds that are off-screen
        this.removeOffscreenClouds();
    }
    
    updateCloudCounts() {
        this.cloudSettings.S1.currentCount = 0;
        this.cloudSettings.M1.currentCount = 0;
        this.cloudSettings.CIRRUS.currentCount = 0;
        
        for (const cloud of this.clouds) {
            if (cloud.templateName === 'S1') {
                this.cloudSettings.S1.currentCount++;
            } else if (cloud.templateName === 'M1') {
                this.cloudSettings.M1.currentCount++;
            } else if (cloud.templateName === 'CIRRUS') {
                this.cloudSettings.CIRRUS.currentCount++;
            }
        }
    }
    
    spawnMissingClouds() {
        // Spawn S1 clouds if needed
        if (this.cloudSettings.S1.currentCount < this.cloudSettings.S1.targetCount) {
            const needed = this.cloudSettings.S1.targetCount - this.cloudSettings.S1.currentCount;
            for (let i = 0; i < needed; i++) {
                this.spawnRandomCloud('S1');
            }
        }
        
        // Spawn M1 clouds if needed
        if (this.cloudSettings.M1.currentCount < this.cloudSettings.M1.targetCount) {
            const needed = this.cloudSettings.M1.targetCount - this.cloudSettings.M1.currentCount;
            for (let i = 0; i < needed; i++) {
                this.spawnRandomCloud('M1');
            }
        }
        
        // Spawn CIRRUS clouds if needed
        if (this.cloudSettings.CIRRUS.currentCount < this.cloudSettings.CIRRUS.targetCount) {
            const needed = this.cloudSettings.CIRRUS.targetCount - this.cloudSettings.CIRRUS.currentCount;
            for (let i = 0; i < needed; i++) {
                this.spawnRandomCloud('CIRRUS');
            }
        }
    }
    
    spawnRandomCloud(templateName) {
        const settings = this.cloudSettings[templateName];
        const minY = settings.minY;
        const maxY = settings.maxY;
        
        // Random position within height range
        const cloudY = Math.floor((Math.random() * (maxY - minY) + minY) / this.gridSize) * this.gridSize;
        
        // Random width and height based on template
        let cloudWidth, cloudHeight, parallaxSpeed;
        
        if (templateName === 'S1') {
            cloudWidth = Math.floor((Math.random() * 40 + 80) / this.gridSize) * this.gridSize; // 80-120px
            cloudHeight = Math.floor((Math.random() * 20 + 20) / this.gridSize) * this.gridSize; // 20-40px
            parallaxSpeed = 0.003;
        } else if (templateName === 'M1') {
            cloudWidth = Math.floor((Math.random() * 40 + 120) / this.gridSize) * this.gridSize; // 120-160px
            cloudHeight = Math.floor((Math.random() * 30 + 60) / this.gridSize) * this.gridSize; // 60-90px
            parallaxSpeed = 0.003;
        } else if (templateName === 'CIRRUS') {
            cloudWidth = Math.floor((Math.random() * 40 + 160) / this.gridSize) * this.gridSize; // 160-200px
            cloudHeight = Math.floor((Math.random() * 10 + 20) / this.gridSize) * this.gridSize; // 20-30px
            parallaxSpeed = 0.001;
        }
        
        // Random X position (spawn off-screen to the right if wind is positive, left if negative)
        let cloudX;
        if (this.windStrength > 0) {
            // Spawn off-screen to the left so clouds drift in
            cloudX = -cloudWidth;
        } else {
            // Spawn off-screen to the right so clouds drift in
            cloudX = this.width;
        }
        
        this.createPixelArtCloud(cloudX, cloudY, cloudWidth, cloudHeight, parallaxSpeed, templateName);
    }
    
    removeOffscreenClouds() {
        this.clouds = this.clouds.filter(cloud => {
            // Remove clouds that are completely off-screen
            const offscreenLeft = cloud.x + cloud.width < 0;
            const offscreenRight = cloud.x > this.width;
            
            if (offscreenLeft || offscreenRight) {
                return false; // Remove this cloud
            }
            return true; // Keep this cloud
        });
    }
    
    regenerateClouds() {
        this.clearAllClouds();
        this.spawnInitialClouds();
    }
    
    clearAllClouds() {
        this.clouds = [];
        this.updateCloudCounts();
    }

    addParticlesAt(x, y, isManualSpawn = false) {
        if (this.behavior === 'fireworks') {
            this.launchFirework(x, y);
            return;
        }

        // Snap to exact grid position - no weird offsets
        const gridX = Math.floor(x / this.gridSize) * this.gridSize;
        const gridY = Math.floor(y / this.gridSize) * this.gridSize;
        
        // For manual spawning, use particlesPerClick; for automatic storm, use random range between smallest and biggest
        const spawnCount = isManualSpawn ? this.particlesPerClick : 
            Math.floor(Math.random() * (this.biggestPieces - this.smallestPieces + 1)) + this.smallestPieces;
        
        const particleOptions = {
            theme: this.theme,
            behavior: this.behavior,
            combineEnabled: this.theme.combineEnabled,
            settleEnabled: this.theme.settleEnabled,
            fadeOut: this.theme.fadeOut,
            tumble: this.theme.tumble,
            colors: this.theme.colors,
        };

        // Spawn multiple particles based on spawnCount
        for (let i = 0; i < spawnCount; i++) {
            // Add small random offset within the grid cell for flurry effect
            const offsetX = (Math.random() - 0.5) * this.gridSize * 0.3;
            const offsetY = (Math.random() - 0.5) * this.gridSize * 0.3;
            
            const spawnX = gridX + offsetX;
            const spawnY = gridY + offsetY;
            
            // Allow particles to spawn above canvas (for snowfall effect)
            if (spawnX >= 0 && spawnX < this.width) {
                // Assign random fall speed between min and max
                const fallSpeed = Math.random() * (this.maxFallSpeed - this.minFallSpeed) + this.minFallSpeed;
                const particle = new SnowParticle(spawnX, spawnY, fallSpeed, particleOptions);
                this.particles.push(particle);
                this.totalFlakesSpawned++; // Increment total flakes counter
                
                // Update the display immediately when a flake is spawned
                const totalFlakesElement = document.getElementById('totalFlakesSpawned');
                if (totalFlakesElement) {
                    totalFlakesElement.textContent = this.totalFlakesSpawned;
                }
                
                // Only add to grid if the position is within grid bounds AND not occupied by structure
                const gridPosX = Math.floor(spawnX / this.gridSize);
                const gridPosY = Math.floor(spawnY / this.gridSize);
                if (gridPosY >= 0 && gridPosY < this.grid.length && gridPosX >= 0 && gridPosX < this.grid[0].length) {
                    // Check if cell is already occupied by structure - don't overwrite!
                    const existingCell = this.grid[gridPosY][gridPosX];
                    if (!existingCell || existingCell.type !== 'structure') {
                    this.grid[gridPosY][gridPosX] = new GridCell('particle', { particle: particle });
                    }
                }
            }
        }
    }

    getMaxFireworkSparks() {
        return this.isMobile ? MAX_FIREWORK_SPARKS_MOBILE : MAX_FIREWORK_SPARKS;
    }

    getMaxVolleyShells() {
        return this.isMobile ? MAX_VOLLEY_SHELLS_MOBILE : MAX_VOLLEY_SHELLS;
    }

    /** Effects the auto show / random picks must not use on this device. */
    getFireworkEffectExclude() {
        return this.isMobile ? ['kamuro'] : [];
    }

    /**
     * Lay out 3 batteries (left / center / right), each with mortar pads.
     * Pads within a battery are tight; batteries are spaced across the viewing corridor.
     * Default lean is gently inward so arcs play toward the audience sightline.
     */
    pickFireworkLaunchSites(
        batteries = this.fireworkBatteryCount,
        padsPerBattery = null
    ) {
        const mobile = !!this.isMobile;
        const groundY = this.height - (mobile ? 10 : 14);
        const zone = getFireworkViewingZone(this.width, this.height, { mobile });
        // On mobile the zone is already near full-bleed — don't inset pads again
        const edgeInset = mobile ? 0.04 : 0.1;
        const padX0 = zone.x0 + (zone.x1 - zone.x0) * edgeInset;
        const padX1 = zone.x1 - (zone.x1 - zone.x0) * edgeInset;
        const usable = Math.max(mobile ? 60 : 80, padX1 - padX0);

        const batteryRoles = ['left', 'center', 'right'];
        const batteryCount = Math.max(1, Math.min(3, batteries | 0));
        const padsSource =
            padsPerBattery != null
                ? padsPerBattery
                : mobile
                    ? 2
                    : this.fireworkPadsPerBattery || 3;
        const padsEach = Math.max(1, Math.min(4, padsSource | 0));
        // Gap between pads inside one battery (~pad width + mortar clearance)
        const padPitch = mobile ? 8 : 12;
        const batterySpan = (padsEach - 1) * padPitch;
        // Battery centers — mobile uses a slightly wider stance so L/R aren't clipped
        const batteryTs =
            batteryCount === 1
                ? [0.5]
                : batteryCount === 2
                    ? mobile
                        ? [0.22, 0.78]
                        : [0.28, 0.72]
                    : mobile
                        ? [0.14, 0.5, 0.86]
                        : [0.18, 0.5, 0.82];

        const sites = [];
        for (let b = 0; b < batteryCount; b++) {
            const role = batteryRoles[b] || 'center';
            const batteryCx = padX0 + usable * batteryTs[b];
            const startX = batteryCx - batterySpan / 2;

            let baseLean = 0;
            if (role === 'left') baseLean = mobile ? 0.08 : 0.12;
            else if (role === 'right') baseLean = mobile ? -0.08 : -0.12;

            for (let s = 0; s < padsEach; s++) {
                // Outer pads lean a touch more toward center
                const slotBias =
                    role === 'left'
                        ? (padsEach > 1 ? (s / (padsEach - 1)) * 0.06 : 0)
                        : role === 'right'
                            ? (padsEach > 1 ? ((padsEach - 1 - s) / (padsEach - 1)) * -0.06 : 0)
                            : (s - (padsEach - 1) / 2) * 0.025;
                const lean = baseLean + slotBias + (Math.random() - 0.5) * 0.04;
                sites.push({
                    id: `${role}-${s}`,
                    role,
                    battery: b,
                    slot: s,
                    x: startX + s * padPitch + (Math.random() - 0.5) * 2,
                    y: groundY,
                    lean,
                    padWidth: mobile ? 8 : 10,
                });
            }
        }

        this.fireworkPadsPerBattery = padsEach;
        this.fireworkLaunchSiteCount = sites.length;
        this.fireworkViewingZone = zone;
        this.fireworkLaunchSites = sites;
        return sites;
    }

    getRandomFireworkLaunchSite() {
        if (!this.fireworkLaunchSites.length) {
            this.pickFireworkLaunchSites();
        }
        return this.fireworkLaunchSites[
            Math.floor(Math.random() * this.fireworkLaunchSites.length)
        ];
    }

    getFireworkSitesByRole(role) {
        if (!this.fireworkLaunchSites.length) this.pickFireworkLaunchSites();
        const list = this.fireworkLaunchSites.filter((s) => s.role === role);
        return list.length ? list : this.fireworkLaunchSites;
    }

    getFireworkSiteByRole(role) {
        const list = this.getFireworkSitesByRole(role);
        return list[Math.floor(Math.random() * list.length)];
    }

    /**
     * Pick a launch X across the pad's ~10px width without reusing the same
     * pixel offset for other shots already taken in this volley.
     */
    pickPadLaunchX(site, usedOffsets) {
        const width = site.padWidth || 10;
        const half = Math.floor(width / 2);
        const keyPrefix = site.id || site.role || 'pad';
        const candidates = [];
        for (let o = -half; o <= half; o++) {
            const key = `${keyPrefix}:${o}`;
            if (!usedOffsets.has(key)) candidates.push(o);
        }
        let offset;
        if (candidates.length) {
            offset = candidates[Math.floor(Math.random() * candidates.length)];
            usedOffsets.add(`${keyPrefix}:${offset}`);
        } else {
            // Pad exhausted for this volley — allow soft reuse
            offset = Math.floor(Math.random() * width) - half;
        }
        return site.x + offset;
    }

    /** Pick a volley size, weighted toward smaller, with quiet beats and rare wall moments. */
    randomVolleySize() {
        const max = this.getMaxVolleyShells();
        if (this.isMobile) {
            const r = Math.random();
            if (r < 0.15) return 0;
            if (r < 0.5) return 1 + Math.floor(Math.random() * 3); // 1–3
            if (r < 0.8) return 4 + Math.floor(Math.random() * 3); // 4–6
            return Math.min(max, 7 + Math.floor(Math.random() * 4)); // 7–10
        }
        const r = Math.random();
        if (r < 0.1) return 0; // breath between volleys
        if (r < 0.38) return 1 + Math.floor(Math.random() * 4); // 1–4
        if (r < 0.62) return 5 + Math.floor(Math.random() * 5); // 5–9
        if (r < 0.8) return 10 + Math.floor(Math.random() * 6); // 10–15
        if (r < 0.93) return 16 + Math.floor(Math.random() * 9); // 16–24
        return 25 + Math.floor(Math.random() * (max - 24)); // 25–36
    }

    clearFinaleTimeouts() {
        if (this.finaleTimeouts?.length) {
            for (const id of this.finaleTimeouts) clearTimeout(id);
        }
        this.finaleTimeouts = [];
    }

    clearVolleyTimeouts() {
        if (this.volleyTimeouts?.length) {
            for (const id of this.volleyTimeouts) clearTimeout(id);
        }
        this.volleyTimeouts = [];
    }

    clearManualTimeouts() {
        if (this.manualTimeouts?.length) {
            for (const id of this.manualTimeouts) clearTimeout(id);
        }
        this.manualTimeouts = [];
    }

    scheduleFireworkTimeout(fn, delay, bucket = 'volley') {
        const tid = setTimeout(fn, delay);
        if (bucket === 'finale') this.finaleTimeouts.push(tid);
        else if (bucket === 'manual') this.manualTimeouts.push(tid);
        else this.volleyTimeouts.push(tid);
        return tid;
    }

    /**
     * Fire an intentionally designed volley (color story + height staging + pattern).
     * Designs mix mono, complementary colors, crossed paths, lead/crown height, etc.
     * @param {number|null} size - shells in this volley (0–36); null = weighted random
     * @param {{intense?: boolean, site?: object, finale?: boolean, pattern?: string, design?: string|object, manual?: boolean}} [opts]
     */
    fireVolley(size = null, opts = {}) {
        if (this.behavior !== 'fireworks') return 0;
        // Manual desk shots never require the auto show to be running
        if (!opts.manual && !this.snowMode) return 0;

        const count = size == null
            ? this.randomVolleySize()
            : Math.max(0, Math.min(this.getMaxVolleyShells(), Math.floor(size)));
        if (count === 0) return 0;

        if (!this.fireworkLaunchSites.length) this.pickFireworkLaunchSites();

        const finale = !!(opts.finale || opts.intense || this.fireworkFinaleActive);
        const manual = !!opts.manual;
        let activeDesign;
        if (typeof opts.design === 'string') {
            activeDesign = VOLLEY_DESIGNS[opts.design] || pickVolleyDesign({ finale });
        } else if (opts.design && typeof opts.design === 'object') {
            activeDesign = opts.design;
        } else {
            activeDesign = pickVolleyDesign({ finale });
        }

        // Spatial pattern: prefer design's list, allow override
        let pattern;
        if (opts.pattern) {
            const p = VOLLEY_PATTERNS[opts.pattern];
            pattern = {
                id: opts.pattern,
                name: manual
                    ? `Manual · ${(p && p.name) || opts.pattern}`
                    : `${activeDesign.name} · ${(p && p.name) || opts.pattern}`,
            };
        } else {
            const preferred = activeDesign.patterns || [];
            const pickId =
                preferred.length > 0
                    ? preferred[Math.floor(Math.random() * preferred.length)]
                    : pickVolleyPattern({ finale }).id;
            const p = VOLLEY_PATTERNS[pickId];
            pattern = {
                id: pickId,
                name: `${activeDesign.name} · ${(p && p.name) || pickId}`,
            };
        }
        this.lastVolleyPatternName = pattern.name || pattern.id;

        const zone =
            this.fireworkViewingZone ||
            getFireworkViewingZone(this.width, this.height, { mobile: this.isMobile });
        const planned = planVolleyShots(
            pattern.id,
            count,
            this.fireworkLaunchSites,
            zone
        );

        const { charges } = resolveVolleyCharges(activeDesign, planned, {
            finale,
            mobile: this.isMobile,
            excludeEffects: this.getFireworkEffectExclude(),
        });
        if (opts.caliberId) {
            const forced = getShellCaliber(opts.caliberId);
            for (const charge of charges) {
                const prev = getShellCaliber(charge.caliberId);
                charge.forceScale =
                    ((charge.forceScale || 1) / (prev.forceScale || 1)) * forced.forceScale;
                charge.gravity =
                    ((charge.gravity || 0.12) / (prev.gravityScale || 1)) *
                    forced.gravityScale;
                charge.caliberId = forced.id;
            }
        }
        const usedPadOffsets = new Set();
        const intensity = opts.intense ? 1.06 : 1;

        for (let i = 0; i < planned.length; i++) {
            const shot = planned[i];
            const charge = charges[i] || charges[0];
            const site = opts.site || shot.site;
            // Design owns timing (tight / rippled / two-wave); keep only light jitter
            const delay =
                (charge.delayBoost || 0) +
                (shot.delayBoost || 0) +
                Math.random() * (opts.intense ? 18 : 28);

            const launchX = this.pickPadLaunchX(site, usedPadOffsets);
            let force = Math.min(
                14,
                Math.max(4.5, shot.force * (charge.forceScale || 1) * intensity)
            );
            const caliber = getShellCaliber(charge.caliberId || opts.caliberId);
            const launchSite = { ...site, x: launchX };
            force = fitForceToViewingZone(
                launchSite,
                shot.angle,
                force,
                charge.gravity || 0.12,
                zone,
                caliber
            );

            const tid = setTimeout(() => {
                if (this.behavior !== 'fireworks') return;
                if (!manual && !this.snowMode) return;
                this.launchFirework(launchX, null, {
                    effectId: charge.effectId,
                    angle: shot.angle,
                    force,
                    color: charge.color,
                    colorB: charge.colorB,
                    gravity: charge.gravity,
                    caliberId: caliber.id,
                    _caliberForceApplied: true,
                    doubleBreak:
                        finale &&
                        charge.wave === 'crown' &&
                        Math.random() < 0.25,
                });
            }, delay);
            if (manual) this.manualTimeouts.push(tid);
            else if (finale) this.finaleTimeouts.push(tid);
            else this.volleyTimeouts.push(tid);
        }
        return planned.length;
    }

    /**
     * Manual mortar desk: fire N shells of one effect from a L/C/R battery.
     * Does not start the automatic show.
     */
    fireShellsFromBattery({
        battery = 'center',
        effectId = 'peony',
        count = 1,
        caliberId = 'five',
    } = {}) {
        if (this.behavior !== 'fireworks') return 0;
        if (!this.fireworkLaunchSites.length) this.pickFireworkLaunchSites();

        const role =
            battery === 'left' || battery === 'right' || battery === 'center'
                ? battery
                : 'center';
        const pads = this.getFireworkSitesByRole(role);
        const n = Math.max(1, Math.min(this.getMaxVolleyShells(), Math.floor(count) || 1));
        const zone =
            this.fireworkViewingZone ||
            getFireworkViewingZone(this.width, this.height, { mobile: this.isMobile });
        const safeEffectId =
            this.isMobile && effectId === 'kamuro' ? 'peony' : effectId;
        const effect = getFireworkEffect(safeEffectId);
        const caliber = getShellCaliber(caliberId);

        for (let i = 0; i < n; i++) {
            const site = pads[i % pads.length];
            const delay = i * (55 + Math.random() * 40);
            const tid = setTimeout(() => {
                if (this.behavior !== 'fireworks') return;
                const aim = aimIntoViewingZone(site, zone, {
                    maxLean: role === 'center' ? 0.35 : 0.55,
                    caliberId: caliber.id,
                    gravity: (0.11 + Math.random() * 0.02) * caliber.gravityScale,
                });
                this.launchFirework(site.x + (Math.random() - 0.5) * 4, null, {
                    effectId: effect.id,
                    angle: aim.angle,
                    force: aim.force,
                    gravity: (0.11 + Math.random() * 0.02) * caliber.gravityScale,
                    caliberId: caliber.id,
                    _caliberForceApplied: true,
                });
            }, delay);
            this.manualTimeouts.push(tid);
        }
        this.lastFireworkEffectName = `${caliber.name} ${effect.name}`;
        this.lastVolleyPatternName = `Manual · ${role[0].toUpperCase()}×${n} · ${caliber.name}`;
        return n;
    }

    /**
     * Manual desk: fire a spatial pattern volley (Cross, Fan, etc.) without starting the show.
     */
    fireManualPattern({ pattern = 'cross', count = 4, caliberId = null } = {}) {
        if (this.behavior !== 'fireworks') return 0;
        const n = Math.max(1, Math.min(this.getMaxVolleyShells(), Math.floor(count) || 1));
        const designByPattern = {
            cross: 'complementCross',
            wings: 'mirroredPair',
            zipper: 'leftRightTalk',
            cascade: 'stairway',
            bouquet: 'monoStatement',
            converse: 'leftRightTalk',
            zee: 'mirroredPair',
        };
        return this.fireVolley(n, {
            manual: true,
            pattern,
            caliberId,
            design: designByPattern[pattern] || 'monoStatement',
        });
    }

    toggleFireworkFinale() {
        if (this.behavior !== 'fireworks') return;
        if (this.fireworkFinaleActive) this.endFireworkFinale();
        else this.startFireworkFinale();
    }

    startFireworkFinale() {
        if (this.behavior !== 'fireworks') return;
        // Ensure the show is running so bursts keep updating
        if (!this.snowMode) {
            this.startSnow();
        }
        // Cancel previous finale cues only — keep the sky drawn
        this.clearFinaleTimeouts();
        this.fireworkFinaleActive = true;
        this.volleysSinceFinale = 0;
        const duration = this.isMobile
            ? 7000 + Math.random() * 4000
            : 10000 + Math.random() * 6000;
        this.fireworkFinaleUntil = performance.now() + duration;

        const finaleBtn = document.getElementById('finaleBtn');
        if (finaleBtn) {
            finaleBtn.textContent = 'Stop Finale';
            finaleBtn.style.background = '#000000';
            finaleBtn.style.borderColor = '#f8f9fa';
            finaleBtn.style.color = '#f8f9fa';
        }

        const runFinaleBurst = () => {
            if (!this.snowMode || !this.fireworkFinaleActive) return;
            if (performance.now() >= this.fireworkFinaleUntil) {
                this.endFireworkFinale();
                return;
            }
            const finalePatterns = [
                'cross',
                'frontage',
                'zee',
                'fan',
                'chase',
                'centerFocus',
                'bouquet',
                'cascade',
                'wings',
                'zipper',
            ];
            const primary = this.isMobile
                ? 5 + Math.floor(Math.random() * 5)
                : 12 + Math.floor(Math.random() * 13);
            this.fireVolley(primary, {
                intense: true,
                finale: true,
                setup: 'uniform',
                pattern: finalePatterns[Math.floor(Math.random() * finalePatterns.length)],
            });
            const doubleChance = this.isMobile ? 0.35 : 0.65;
            if (this.fireworkLaunchSites.length > 1 && Math.random() < doubleChance) {
                const secondary = this.isMobile
                    ? 3 + Math.floor(Math.random() * 4)
                    : 8 + Math.floor(Math.random() * 10);
                this.fireVolley(secondary, {
                    intense: true,
                    finale: true,
                    setup: 'uniform',
                    pattern: finalePatterns[Math.floor(Math.random() * finalePatterns.length)],
                });
            }
            const next = this.isMobile
                ? 420 + Math.random() * 460
                : 320 + Math.random() * 380;
            this.scheduleFireworkTimeout(runFinaleBurst, next, 'finale');
        };

        runFinaleBurst();
    }

    endFireworkFinale() {
        this.fireworkFinaleActive = false;
        this.fireworkFinaleUntil = 0;
        // Cancel finale cues only — leave rockets/sparks to finish naturally
        this.clearFinaleTimeouts();
        this.volleysSinceFinale = 0;

        const finaleBtn = document.getElementById('finaleBtn');
        if (finaleBtn) {
            finaleBtn.textContent = 'Finale';
            finaleBtn.style.background = '';
            finaleBtn.style.borderColor = '';
            finaleBtn.style.color = '';
        }
    }

    /** Launch a firework rocket with ballistic angle + force + gravity */
    launchFirework(x = null, y = null, options = {}) {
        const groundY = this.height - 14;
        // Auto/random spawn: from a fixed launch site. Explicit x (click) aims from that pad/point.
        const fromSite = x == null;
        const site = fromSite ? this.getRandomFireworkLaunchSite() : null;
        const launchX = fromSite ? site.x : x;

        // Angle: 0 = right, -PI/2 = straight up. Spread for variety of arcs.
        let angle = options.angle;
        let force = options.force;

        if (angle == null || force == null) {
            if (x != null && y != null) {
                // Aim roughly toward the click (from ground under that X)
                const dx = x - launchX;
                const dy = Math.min(y, groundY - 20) - groundY; // always upward-ish
                angle = Math.atan2(dy, dx === 0 ? 0.0001 : dx);
                // Keep launches mostly skyward (about 35°–145° from +x in screen space with y-down)
                const minA = -Math.PI + 0.35;
                const maxA = -0.35;
                angle = Math.max(minA, Math.min(maxA, angle));
                // Higher click → more force / altitude
                const climb = Math.max(40, groundY - y);
                force = 5 + (climb / this.height) * 14 + Math.random() * 1.5;
            } else {
                // Auto-launch: random loft and lean
                angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.15; // ~±33° from vertical
                force = 5.5 + Math.random() * 9; // wider altitude spread
            }
        }

        const effect = options.effectId
            ? getFireworkEffect(
                  this.isMobile && options.effectId === 'kamuro'
                      ? 'peony'
                      : options.effectId
              )
            : pickFireworkEffect({
                  finale: !!this.fireworkFinaleActive,
                  exclude: this.getFireworkEffectExclude(),
              });
        const caliber = getShellCaliber(
            options.caliberId ||
                (this.fireworkFinaleActive
                    ? pickShellCaliber({
                          finale: true,
                          prefer: this.isMobile ? ['five', 'eight'] : null,
                      }).id
                    : pickShellCaliber({
                          prefer: this.isMobile ? ['three', 'five'] : null,
                      }).id)
        );
        this.lastFireworkEffectName = `${caliber.name} ${effect.name}`;

        const palette = effect.colors || this.theme.colors || ['#ff5252', '#ffeb3b', '#40c4ff'];
        const color =
            options.color ||
            palette[Math.floor(Math.random() * palette.length)];
        const colorB =
            options.colorB ||
            (effect.colorsB || palette)[
                Math.floor(Math.random() * (effect.colorsB || palette).length)
            ];

        // Apply caliber loft unless the caller already baked it into force
        if (!options._caliberForceApplied) {
            force = Math.min(14, force * caliber.forceScale);
        }
        const gravity =
            options.gravity != null
                ? options.gravity
                : (0.11 + Math.random() * 0.04) * caliber.gravityScale;

        // Keep breaks inside the viewing window for this caliber
        const zone =
            this.fireworkViewingZone || getFireworkViewingZone(this.width, this.height);
        force = fitForceToViewingZone(
            { x: launchX, y: groundY, role: 'center' },
            angle,
            force,
            gravity,
            zone,
            caliber
        );

        this.rockets.push({
            x: launchX,
            y: groundY,
            vx: Math.cos(angle) * force + this.windStrength * 0.04,
            vy: Math.sin(angle) * force,
            gravity,
            angle,
            force,
            color,
            colorB,
            effectId: effect.id,
            caliberId: caliber.id,
            risingTail: !!effect.risingTail,
            doubleBreak: !!options.doubleBreak || !!effect.secondaryBurst,
            trail: [],
            hasLeftPad: false,
        });
        this.totalFlakesSpawned++;
        const totalFlakesElement = document.getElementById('totalFlakesSpawned');
        if (totalFlakesElement) {
            totalFlakesElement.textContent = this.totalFlakesSpawned;
        }
    }

    /**
     * Queue a spark for commit after the update loop.
     * (Committing mid-loop while shift()-ing the array was wiping the sky.)
     */
    pushSpark(spark) {
        if (!this._pendingSparks) this._pendingSparks = [];
        this._pendingSparks.push(spark);
    }

    /**
     * Commit queued sparks with safe cap policy:
     * - glitter / crossette children never evict other show sparks
     * - only glitter crumbs may be dropped to make room for new primaries
     */
    flushPendingSparks() {
        if (!this._pendingSparks?.length) return;
        const pending = this._pendingSparks;
        this._pendingSparks = [];

        for (const spark of pending) {
            this.commitSpark(spark);
        }
    }

    /**
     * Make room for an incoming burst so long-lived kamuro/willow don't
     * permanently starve later shells of spark slots.
     */
    ensureSparkHeadroom(needed) {
        const max = this.getMaxFireworkSparks();
        const target = Math.max(0, max - Math.max(8, needed | 0));
        while (this.sparks.length > target) {
            const glitterIdx = this.sparks.findIndex((s) => s.isGlitter);
            if (glitterIdx >= 0) {
                this.sparks.splice(glitterIdx, 1);
                continue;
            }
            // Drop the weakest remaining star (nearly dead first)
            let victim = 0;
            let worst = this.sparks[0]?.life ?? 1;
            const scan = Math.min(this.sparks.length, 48);
            for (let i = 1; i < scan; i++) {
                if (this.sparks[i].life < worst) {
                    worst = this.sparks[i].life;
                    victim = i;
                }
            }
            this.sparks.splice(victim, 1);
        }
    }

    commitSpark(spark) {
        if (this.sparks.length >= this.getMaxFireworkSparks()) {
            // Free glitter crumbs first
            const glitterIdx = this.sparks.findIndex((s) => s.isGlitter);
            if (glitterIdx >= 0) {
                this.sparks.splice(glitterIdx, 1);
            } else if (spark.isCrossetteChild) {
                const tipIdx = this.sparks.findIndex((s) => s.isCrossetteChild);
                if (tipIdx >= 0) this.sparks.splice(tipIdx, 1);
                else return false;
            } else if (spark.isGlitter) {
                return false;
            } else {
                // New primary: reclaim the weakest existing spark so bursts keep firing
                let victim = 0;
                let worst = this.sparks[0]?.life ?? 1;
                const scan = Math.min(this.sparks.length, 48);
                for (let i = 1; i < scan; i++) {
                    if (this.sparks[i].life < worst) {
                        worst = this.sparks[i].life;
                        victim = i;
                    }
                }
                this.sparks.splice(victim, 1);
            }
        }

        this.sparks.push(spark);
        return true;
    }

    createSparkFromRecipe(rocket, effect, angle, speed, overrides = {}) {
        const palette = effect.colors || this.theme.colors || ['#ff5252', '#ffeb3b', '#40c4ff'];
        const burstColor = rocket.color || palette[Math.floor(Math.random() * palette.length)];
        const secondary = palette[Math.floor(Math.random() * palette.length)];
        const colorBPalette = effect.colorsB || palette;
        const colorB =
            overrides.colorB ||
            rocket.colorB ||
            colorBPalette[Math.floor(Math.random() * colorBPalette.length)];
        const caliber = getShellCaliber(rocket.caliberId);
        const rawSize =
            overrides.size != null
                ? overrides.size
                : fireworkUtil.randRange(effect.size);

        return {
            x: rocket.x,
            y: rocket.y,
            vx: Math.cos(angle) * speed + this.windStrength * 0.08,
            vy: Math.sin(angle) * speed * 0.85,
            life: 1,
            decay: fireworkUtil.randRange(effect.decay) / Math.sqrt(caliber.sizeScale),
            color: overrides.color || rocket.color || (Math.random() < 0.7 ? burstColor : secondary),
            colorB,
            colorSwapAt: effect.colorSwapAt ?? null,
            size: rawSize * caliber.sizeScale,
            effectId: effect.id,
            drag: effect.drag,
            sparkGravity: effect.sparkGravity * (0.92 + 0.08 / caliber.sizeScale),
            trail: effect.trail ? [] : null,
            trailLength: effect.trailLength || 0,
            blink: !!effect.blink,
            blinkPeriod: effect.blink
                ? Math.floor(fireworkUtil.randRange(effect.blinkPeriod || [4, 9]))
                : 0,
            glitter: !!effect.glitter,
            age: 0,
            splitAt: effect.splitAfter
                ? Math.floor(fireworkUtil.randRange(effect.splitAfter))
                : null,
            isCrossetteChild: false,
            isGlitter: false,
            ...overrides,
            // Keep caliber-scaled size even if overrides.size was provided
            size: rawSize * caliber.sizeScale,
            color: overrides.color || rocket.color || (Math.random() < 0.7 ? burstColor : secondary),
            colorB,
        };
    }

    burstFirework(rocket, effectOverride = null) {
        const effect = effectOverride || getFireworkEffect(rocket.effectId || 'peony');
        const caliber = getShellCaliber(rocket.caliberId);
        this.lastFireworkEffectName = `${caliber.name} ${effect.name}`;
        const forceBoost = Math.min(1.55, (rocket.force || 8) / 10);
        const mobileSparkMul = this.isMobile ? 0.62 : 1;
        const sparkCount = Math.min(
            this.isMobile ? 52 : 90,
            Math.floor(
                fireworkUtil.randRange(effect.sparkCount) *
                    forceBoost *
                    caliber.sparkScale *
                    mobileSparkMul
            )
        );
        // Kamuro/willow linger; free slots so this burst can actually land
        this.ensureSparkHeadroom(sparkCount + 24);
        const speedMul = forceBoost * caliber.speedScale;

        if (effect.spawnShape === 'ring') {
            const radiusSpeed = fireworkUtil.randRange(effect.speed) * speedMul;
            // Slight ellipse tilt for a more 3D ring
            const tilt = 0.55 + Math.random() * 0.35;
            for (let i = 0; i < sparkCount; i++) {
                const a = (Math.PI * 2 * i) / sparkCount;
                const speed = radiusSpeed * (0.92 + Math.random() * 0.16);
                this.pushSpark(this.createSparkFromRecipe(rocket, effect, a, speed, {
                    vy: Math.sin(a) * speed * tilt,
                }));
            }
        } else if (effect.spawnShape === 'palm') {
            const fronds = sparkCount;
            for (let i = 0; i < fronds; i++) {
                // Upper-hemisphere fan of heavy comets
                const a = -Math.PI + 0.35 + (Math.PI - 0.7) * (i / Math.max(1, fronds - 1))
                    + (Math.random() - 0.5) * 0.15;
                const speed = fireworkUtil.randRange(effect.speed) * speedMul;
                this.pushSpark(this.createSparkFromRecipe(rocket, effect, a, speed, {
                    size: fireworkUtil.randRange(effect.size) + 2,
                }));
            }
        } else if (effect.spawnShape === 'horsetail') {
            // Soft pour: mostly downward/outward, then heavy fall (waterfall curtain)
            for (let i = 0; i < sparkCount; i++) {
                const a =
                    Math.PI * 0.15 +
                    Math.random() * Math.PI * 0.7 +
                    (Math.random() < 0.5 ? 0 : Math.PI); // prefer lower hemisphere
                const speed = fireworkUtil.randRange(effect.speed) * speedMul;
                const spark = this.createSparkFromRecipe(rocket, effect, a, speed);
                // Bias velocity downward so the cascade reads as a pour
                spark.vy = Math.abs(spark.vy) * 0.35 + speed * 0.55;
                spark.vx *= 0.75;
                this.pushSpark(spark);
            }
        } else {
            // Sphere / peony-family (incl. kamuro)
            for (let i = 0; i < sparkCount; i++) {
                const a = (Math.PI * 2 * i) / sparkCount + Math.random() * 0.2;
                const speed = fireworkUtil.randRange(effect.speed) * speedMul;
                this.pushSpark(this.createSparkFromRecipe(rocket, effect, a, speed));
            }
        }

        // Pistil: dense contrasting core
        if (effect.id === 'pistil') {
            const centerN = Math.floor(
                fireworkUtil.randRange(effect.centerCount || [10, 16]) * caliber.sparkScale
            );
            const centerColors = effect.centerColors || ['#ffffff', '#fff9c4'];
            for (let i = 0; i < centerN; i++) {
                const a = Math.random() * Math.PI * 2;
                const speed = fireworkUtil.randRange(effect.centerSpeed || [0.2, 0.8]);
                this.pushSpark(this.createSparkFromRecipe(rocket, effect, a, speed, {
                    color: centerColors[Math.floor(Math.random() * centerColors.length)],
                    size: 3 + Math.random() * 3,
                    decay: 0.018 + Math.random() * 0.02,
                }));
            }
        }

        // Double-break: secondary peony/strobe a moment later
        if (rocket.doubleBreak) {
            const nested = pickFireworkEffect({
                prefer: ['peony', 'strobe', 'chrysanthemum', 'glitter'],
            });
            this.scheduleFireworkTimeout(() => {
                if (!this.snowMode || this.behavior !== 'fireworks') return;
                this.burstFirework(
                    {
                        ...rocket,
                        color: nested.colors?.[0] || rocket.color,
                        doubleBreak: false,
                    },
                    nested
                );
            }, 220 + Math.random() * 220, this.fireworkFinaleActive ? 'finale' : 'volley');
        }
    }

    splitCrossette(parent) {
        // Always fire the classic 4-tip X. Safe now because tips are queued and
        // committed after the update loop (mid-loop shift() was the wipe).
        const tipSpeed = 1.9 + Math.random() * 1.5;
        // Offset by 45° so the limbs read as an X, not a +.
        const baseAngle = Math.atan2(parent.vy, parent.vx) + Math.PI / 4;
        for (let k = 0; k < 4; k++) {
            const a = baseAngle + (Math.PI / 2) * k;
            this.pushSpark({
                x: parent.x,
                y: parent.y,
                vx: Math.cos(a) * tipSpeed,
                vy: Math.sin(a) * tipSpeed,
                life: Math.min(1, parent.life + 0.35),
                decay: parent.decay * 1.05,
                color: parent.color,
                colorB: parent.colorB,
                colorSwapAt: null,
                size: Math.max(3.5, parent.size * 0.85),
                effectId: 'crossette',
                drag: 0.988,
                sparkGravity: 0.065,
                trail: [],
                trailLength: 6,
                blink: false,
                blinkPeriod: 0,
                glitter: false,
                age: 0,
                splitAt: null,
                isCrossetteChild: true,
                isGlitter: false,
            });
        }
    }

    updateFireworks() {
        const groundY = this.height - 10;

        for (let i = this.rockets.length - 1; i >= 0; i--) {
            const r = this.rockets[i];
            r.trail.push({ x: r.x, y: r.y, gold: !!r.risingTail });
            if (r.trail.length > (r.risingTail ? 14 : 10)) r.trail.shift();

            // Ballistic flight: gravity + light wind
            r.vx += this.windStrength * 0.002;
            r.vy += r.gravity;
            r.x += r.vx;
            r.y += r.vy;

            if (r.y < groundY - 40) {
                r.hasLeftPad = true;
            }

            const atApex = r.hasLeftPad && r.vy >= 0;
            const offScreen = r.x < -40 || r.x > this.width + 40;
            const hitGround = r.hasLeftPad && r.y >= groundY;

            if (atApex || (offScreen && r.hasLeftPad) || hitGround) {
                this.burstFirework(r);
                this.rockets.splice(i, 1);
            }
        }

        for (let i = this.sparks.length - 1; i >= 0; i--) {
            const s = this.sparks[i];
            s.age = (s.age || 0) + 1;

            if (s.trail) {
                s.trail.push({ x: s.x, y: s.y });
                if (s.trail.length > s.trailLength) s.trail.shift();
            }

            // Crossette primary → four children
            if (s.splitAt != null && !s.isCrossetteChild && s.age >= s.splitAt) {
                this.splitCrossette(s);
                this.sparks.splice(i, 1);
                continue;
            }

            // Glitter: sprinkle tiny crumbs (rate-limited so we don't thrash the spark cap)
            if (
                s.glitter &&
                !s.isGlitter &&
                s.age % 4 === 0
            ) {
                const fx = getFireworkEffect(s.effectId);
                const chance = fx.glitterChance != null ? fx.glitterChance : 0.12;
                if (Math.random() < chance) {
                    this.pushSpark({
                        x: s.x,
                        y: s.y,
                        vx: s.vx * 0.2 + (Math.random() - 0.5) * 0.8,
                        vy: s.vy * 0.2 + Math.random() * 0.5,
                        life: 0.35 + Math.random() * 0.35,
                        decay: 0.04 + Math.random() * 0.04,
                        color: '#fffde7',
                        size: 2 + Math.random() * 2,
                        effectId: 'glitter',
                        drag: 0.97,
                        sparkGravity: 0.08,
                        trail: null,
                        trailLength: 0,
                        blink: false,
                        blinkPeriod: 0,
                        glitter: false,
                        age: 0,
                        splitAt: null,
                        isCrossetteChild: false,
                        isGlitter: true,
                    });
                }
            }

            // Color-change mid-life
            if (s.colorSwapAt != null && s.life <= s.colorSwapAt && s.colorB) {
                s.color = s.colorB;
                s.colorSwapAt = null;
            }

            s.vx += this.windStrength * 0.01;
            s.vy += s.sparkGravity != null ? s.sparkGravity : 0.06;
            if (s.drag) {
                s.vx *= s.drag;
                s.vy *= s.drag;
            }
            s.x += s.vx;
            s.y += s.vy;
            s.life -= s.decay;
            if (s.life <= 0 || s.y > this.height || s.x < -20 || s.x > this.width + 20) {
                this.sparks.splice(i, 1);
            }
        }

        // Commit deferred sparks (crossette tips, glitter, etc.) AFTER iteration
        this.flushPendingSparks();
    }

    renderFireworks() {
        for (const r of this.rockets) {
            const caliber = getShellCaliber(r.caliberId);
            const tw = caliber.trailDot;
            for (let t = 0; t < r.trail.length; t++) {
                const p = r.trail[t];
                const alpha = (t + 1) / r.trail.length;
                if (p.gold || r.risingTail) {
                    this.ctx.fillStyle = `rgba(255, 200, 80, ${alpha * 0.55})`;
                    this.ctx.fillRect(p.x, p.y, tw + 1, tw + 1);
                } else {
                    this.ctx.fillStyle = `rgba(255, 220, 160, ${alpha * 0.5})`;
                    this.ctx.fillRect(p.x, p.y, tw, tw);
                }
            }
            const rw = caliber.rocketW;
            const rh = caliber.rocketH;
            this.ctx.fillStyle = '#fff6e0';
            this.ctx.fillRect(r.x - rw / 2, r.y - rh / 2, rw, rh);
            // Bright nose for big shells so caliber reads in ascent
            if (caliber.id === 'eight' || caliber.id === 'twelve') {
                this.ctx.fillStyle = '#ffecb3';
                this.ctx.fillRect(r.x - rw / 2 - 1, r.y - rh / 2 - 2, rw + 2, 3);
            }
        }

        for (const s of this.sparks) {
            // Strobe blink
            if (s.blink && s.blinkPeriod > 0) {
                const phase = Math.floor(s.age / s.blinkPeriod) % 2;
                if (phase === 1) continue;
            }

            if (s.trail?.length) {
                for (let t = 0; t < s.trail.length; t++) {
                    const p = s.trail[t];
                    const ta = ((t + 1) / s.trail.length) * Math.max(0, s.life) * 0.45;
                    this.ctx.globalAlpha = ta;
                    this.ctx.fillStyle = s.color;
                    this.ctx.fillRect(p.x, p.y, Math.max(2, s.size * 0.4), Math.max(2, s.size * 0.4));
                }
            }

            const alpha = Math.max(0, s.life);
            this.ctx.globalAlpha = alpha;
            this.ctx.fillStyle = s.color;
            const sz = Math.max(s.isGlitter ? 2 : 4, s.size * (0.6 + alpha * 0.4));
            this.ctx.fillRect(s.x, s.y, sz, sz);
        }
        this.ctx.globalAlpha = 1;
    }

    renderFireworkLaunchPads() {
        if (!this.fireworkLaunchSites?.length) return;
        for (const site of this.fireworkLaunchSites) {
            const w = site.padWidth || 10;
            this.ctx.globalAlpha = 0.5;
            this.ctx.fillStyle = '#ffcc80';
            this.ctx.fillRect(site.x - w / 2, site.y - 2, w, 4);
            // lean indicator: short tick pointing along pad bias
            const leanX = Math.sin(site.lean) * 10;
            this.ctx.fillStyle = '#ffe0b2';
            this.ctx.fillRect(site.x + leanX - 1, site.y - 10, 2, 8);
        }
        this.ctx.globalAlpha = 1;
    }

    startSpawning(x, y) {
        if (this.behavior === 'fireworks') {
            // Launch immediately, then keep launching while held so several
            // rockets can share the sky (same idea as snow's continuous spawn).
            this.launchFirework(x, y);
            if (this.manualSpawnInterval) return;
            this.manualSpawnInterval = setInterval(() => {
                if (this.isRunning) {
                    this.launchFirework(this.currentSpawnX, this.currentSpawnY);
                }
            }, 180);
            return;
        }

        if (this.manualSpawnInterval) return; // Already spawning manually
        
        // Spawn first batch immediately
        this.addParticlesAt(x, y, true); // true = manual spawn
        
        // Start continuous manual spawning
        this.manualSpawnInterval = setInterval(() => {
            if (this.isRunning) {
                this.addParticlesAt(this.currentSpawnX, this.currentSpawnY, true); // true = manual spawn
            }
        }, this.spawnDelay);
    }

    stopSpawning() {
        if (this.manualSpawnInterval) {
            clearInterval(this.manualSpawnInterval);
            this.manualSpawnInterval = null;
        }
    }

    toggleSnow() {
        if (this.snowMode) {
            this.stopSnow();
        } else {
            this.startSnow();
        }
    }

    getActionLabel() {
        return this.theme?.actionLabel || 'Snow Fall';
    }

    getStopLabel() {
        return this.theme?.stopLabel || 'Stop Snow';
    }

    startSnow() {
        this.snowMode = true;
        const rainBtn = document.getElementById('rainBtn');
        if (rainBtn) {
            rainBtn.textContent = this.getStopLabel();
            rainBtn.style.background = '#dc3545';
            rainBtn.style.borderColor = '#dc3545';
            rainBtn.style.color = '#fff';
        }
        
        if (this.spawnInterval) {
            clearInterval(this.spawnInterval);
        }
        
        // Get current flakes per minute setting
        const slider = document.getElementById('flakesPerMinuteSlider');
        const flakesPerMinute = slider ? parseInt(slider.value) : this.baseFlakesPerMinute;
        this.updateSnowfallRate(flakesPerMinute);
    }
    
    updateSnowfallRate(flakesPerMinute) {
        if (this.spawnInterval) {
            clearInterval(this.spawnInterval);
        }
        
        const rate = Math.max(1, flakesPerMinute);
        // Calculate spawn delay based on rate per minute
        const spawnDelay = Math.max(16, Math.floor(60000 / rate));
        
        this.spawnInterval = setInterval(() => {
            if (!this.snowMode) return;
            if (this.behavior === 'fireworks') {
                // Finale runs on its own cue loop when manually started
                if (this.fireworkFinaleActive) {
                    if (performance.now() >= this.fireworkFinaleUntil) {
                        this.endFireworkFinale();
                    }
                    return;
                }

                this.volleysSinceFinale = (this.volleysSinceFinale || 0) + 1;
                // Normal show: each tick is a volley of 0–36 shells from the mortar field
                this.fireVolley();
            } else {
                const x = Math.random() * this.canvas.width;
                const y = 0; // Start at the very top edge of canvas
                this.addParticlesAt(x, y);
            }
        }, spawnDelay);
    }

    stopSnow() {
        this.snowMode = false;
        if (this.fireworkFinaleActive) {
            this.endFireworkFinale();
        }
        this.clearFinaleTimeouts();
        this.clearVolleyTimeouts();
        const rainBtn = document.getElementById('rainBtn');
        if (rainBtn) {
            rainBtn.textContent = this.getActionLabel();
            rainBtn.style.background = '';
            rainBtn.style.borderColor = '';
            rainBtn.style.color = '';
        }
        if (this.spawnInterval) {
            clearInterval(this.spawnInterval);
            this.spawnInterval = null;
        }
    }



    clearAll() {
        this.particles = [];
        this.rockets = [];
        this.sparks = [];
        this._pendingSparks = [];
        if (this.fireworkFinaleActive) {
            this.endFireworkFinale();
        }
        this.clearFinaleTimeouts();
        this.clearVolleyTimeouts();
        this.clearManualTimeouts();
        this.volleysSinceFinale = 0;
        this.grid = this.createGrid();
        this.clouds = []; // Clear clouds from separate array
        this.updateGrid(); // Re-add structures and particles
        this.stopSnow();
        this.stopSpawning();
        
        // Reset accumulated flakes counter
        this.totalFlakesSpawned = 0;
        const totalFlakesElement = document.getElementById('totalFlakesSpawned');
        if (totalFlakesElement) {
            totalFlakesElement.textContent = '0';
        }
    }

    // Calculate estimated total pieces per minute based on spawn rate and piece range
    calculateEstimatedPiecesPerMinute(spawnRate) {
        const avgPiecesPerSpawn = (this.smallestPieces + this.biggestPieces) / 2;
        return Math.round(spawnRate * avgPiecesPerSpawn);
    }

    // Update the estimated flakes per minute stat display
    updateEstimatedFlakesStat() {
        const estimatedFlakesElement = document.getElementById('estimatedFlakesPerMinute');
        if (estimatedFlakesElement) {
            const flakesPerMinuteSlider = document.getElementById('flakesPerMinuteSlider');
            const currentFlakesPerMinute = flakesPerMinuteSlider ? parseInt(flakesPerMinuteSlider.value) || this.baseFlakesPerMinute : this.baseFlakesPerMinute;
            const estimatedPiecesPerMinute = this.calculateEstimatedPiecesPerMinute(currentFlakesPerMinute);
            estimatedFlakesElement.textContent = estimatedPiecesPerMinute;
        }
    }

    update(deltaTime) {
        if (this.behavior === 'fireworks') {
            this.frameCount++;
            this.updateFireworks();
            this.updateStats();
            this.applyWindEffects();
            if (this.frameCount % 60 === 0) {
                this.manageClouds();
            }
            if (this.stormVarianceEnabled && this.frameCount % 60 === 0) {
                this.applyStormVariance();
            }
            this.updateWindGrid();
            return;
        }

        // Reset combination flags for all particles at the start of each frame
        for (const particle of this.particles) {
            particle.combined = false;
        }
        
        // Update particles and remove ones that fell off the bottom
        for (let i = this.particles.length - 1; i >= 0; i--) {
            const particle = this.particles[i];
            particle.update(this.grid, this.width, this.height, this.gridSize);
            
            // Remove particles that fell off the bottom only
            if (!particle.active) {
                // Clean up grid position
                const gridX = Math.floor(particle.x / this.gridSize);
                const gridY = Math.floor(particle.y / this.gridSize);
                if (gridY >= 0 && gridY < this.grid.length && gridX >= 0 && gridX < this.grid[0].length) {
                    if (this.grid[gridY][gridX]?.type === 'particle') {
                        this.grid[gridY][gridX] = new GridCell();
                    }
                }
                
                // Remove from particles array
                this.particles.splice(i, 1);
            }
        }
        
        // Periodic stability check every 4 frames
        this.frameCount++;
        if (this.frameCount % this.stabilityCheckInterval === 0) {
            this.checkStability();
        }
        
        this.updateStats();
        this.applyWindEffects(); // Apply wind effects to clouds and snow
        
        // Manage clouds (spawn/despawn based on settings)
        if (this.frameCount % 60 === 0) { // Check every 60 frames (about once per second)
            this.manageClouds();
        }
        
        // Apply storm variance effects (only every 60 frames, about once per second)
        if (this.stormVarianceEnabled && this.frameCount % 60 === 0) {
            this.applyStormVariance();
        }
        
        // Update wind grid every frame for smooth wind effects
        this.updateWindGrid();
    }

    applyStormVariance() {
        // Only apply variance if storm variance is enabled
        if (!this.stormVarianceEnabled) {
            // Reset to base values when disabled
            this.windStrength = this.baseWindStrength;
            this.updateSnowfallRate(this.baseFlakesPerMinute);
            this.updateWindDisplay(this.windStrength);
            this.updateSnowfallDisplay(this.baseFlakesPerMinute);
            return;
        }
        
        // Apply smooth variance to wind strength
        if (this.windVariance > 0) {
            // Initialize variance target if it doesn't exist
            if (typeof this.windVarianceTarget === 'undefined') {
                this.windVarianceTarget = this.baseWindStrength;
            }
            
            // Only pick a new target occasionally (every 5-10 seconds)
            if (this.frameCount % 300 === 0 || Math.abs(this.windStrength - this.windVarianceTarget) < 0.1) {
                // Pick a new target within the variance range
                const maxVariation = this.windVariance;
                const targetVariation = (Math.random() - 0.5) * maxVariation * 2; // -variance to +variance
                this.windVarianceTarget = this.baseWindStrength + targetVariation;
            }
            
            // Smoothly move toward the target with speed constraint
            const maxChange = this.varianceSpeed * 0.2; // Maximum change per frame (0.2 wind per speed unit)
            const targetChange = this.windVarianceTarget - this.windStrength;
            const change = Math.max(-maxChange, Math.min(maxChange, targetChange)); // Clamp to max change
            this.windStrength += change;
            
            // Update wind display with variance percentage and decimal precision
            const windValue = document.getElementById('windValue');
            if (this.windStrength > 0) {
                const displayValue = Number.isInteger(this.windStrength) ? this.windStrength : this.windStrength.toFixed(1);
                windValue.textContent = `${displayValue} >`;
            } else if (this.windStrength < 0) {
                const displayValue = Number.isInteger(this.windStrength) ? Math.abs(this.windStrength) : Math.abs(this.windStrength).toFixed(1);
                windValue.textContent = `< ${displayValue}`;
            } else {
                windValue.textContent = '0';
            }
            
            // Update the wind variance display
            this.updateWindDisplay(this.windStrength);
        }
        
        // Apply smooth variance to snowfall rate
        if (this.snowfallVariance > 0) {
            // Initialize variance target if it doesn't exist
            if (typeof this.snowfallVarianceTarget === 'undefined') {
                this.snowfallVarianceTarget = this.baseFlakesPerMinute;
            }
            
            // Only pick a new target occasionally (every 5-10 seconds)
            if (this.frameCount % 300 === 0 || Math.abs(this.flakesPerMinute - this.snowfallVarianceTarget) < 10) {
                // Pick a new target within the variance range
                const maxVariation = this.snowfallVariance;
                const targetVariation = (Math.random() - 0.5) * maxVariation * 2; // -variance to +variance
                this.snowfallVarianceTarget = this.baseFlakesPerMinute + targetVariation;
            }
            
            // Smoothly move toward the target with speed constraint
            const maxChange = this.varianceSpeed * 4; // Maximum change per frame (4 flakes per speed unit)
            const targetChange = this.snowfallVarianceTarget - this.flakesPerMinute;
            const change = Math.max(-maxChange, Math.min(maxChange, targetChange)); // Clamp to max change
            this.flakesPerMinute = Math.max(50, this.flakesPerMinute + change);
            
            // Only update spawn interval if flakes per minute changed significantly (more than 5 flakes difference)
            const currentSpawnRate = this.spawnInterval ? this.currentSpawnRate || 500 : 500;
            if (this.snowMode && this.spawnInterval && Math.abs(this.flakesPerMinute - currentSpawnRate) > 5) {
                this.currentSpawnRate = Math.floor(this.flakesPerMinute);
                this.updateSnowfallRate(this.currentSpawnRate);
            }
            
            // Update the display
            this.updateSnowfallDisplay(this.flakesPerMinute);
        }
    }

    updateSnowfallDisplay(currentRate) {
        const currentFlakesPerMinute = document.getElementById('currentFlakesPerMinute');
        const variancePercentage = document.getElementById('variancePercentage');
        
        // Update current rate display
        if (currentFlakesPerMinute) {
            currentFlakesPerMinute.textContent = Math.floor(currentRate);
        }
        
        // Calculate and display variance percentage
        if (variancePercentage) {
            if (this.stormVarianceEnabled && this.snowfallVariance > 0) {
                const variation = currentRate - this.baseFlakesPerMinute;
                const percentage = (variation / this.snowfallVariance) * 100;
                const sign = variation >= 0 ? '+' : '';
                variancePercentage.textContent = `[${sign}${percentage.toFixed(0)}%]`;
            } else {
                variancePercentage.textContent = '';
            }
        }
    }

    updateWindDisplay(currentWind) {
        const windVariancePercentage = document.getElementById('windVariancePercentage');
        const windBaseValue = document.getElementById('windBaseValue');
        
        // Update base wind display (the setting value) - always show the base
        if (this.baseWindStrength > 0) {
            const displayValue = Number.isInteger(this.baseWindStrength) ? this.baseWindStrength : this.baseWindStrength.toFixed(1);
            windBaseValue.textContent = `${displayValue}`;
        } else if (this.baseWindStrength < 0) {
            const displayValue = Number.isInteger(this.baseWindStrength) ? Math.abs(this.baseWindStrength) : Math.abs(this.baseWindStrength).toFixed(1);
            windBaseValue.textContent = `-${displayValue}`;
        } else {
            windBaseValue.textContent = '0';
        }
        
        // Calculate and display variance percentage only when enabled
        if (this.stormVarianceEnabled && this.windVariance > 0) {
            const variation = currentWind - this.baseWindStrength;
            const percentage = (variation / this.windVariance) * 100;
            const sign = variation >= 0 ? '+' : '';
            windVariancePercentage.textContent = `[${sign}${percentage.toFixed(0)}%]`;
        } else {
            windVariancePercentage.textContent = '';
        }
    }

    checkStability() {
        for (const p of this.particles) {
            if (p.settled) {
                // Simple check: if it can move down, it has no support
                if (p.canMoveTo(p.x, p.y + this.gridSize, this.grid, this.width, this.height, this.gridSize)) {
                    p.settled = false;
                }
            }
        }
    }



    updateStats() {
        const particleCountElement = document.getElementById('particleCount');
        if (particleCountElement) {
            const count = this.behavior === 'fireworks'
                ? this.rockets.length + this.sparks.length
                : this.particles.length;
            particleCountElement.textContent = count;
        }
        

        
        // Calculate snow combination statistics
        let totalSnowPieces = 0;
        let largestChunk = 0;
        let chunksBySize = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0 };
        
        for (const particle of this.particles) {
            totalSnowPieces += particle.size;
            largestChunk = Math.max(largestChunk, particle.size);
            chunksBySize[particle.size]++;
        }
        
        // Display snow stats if the element exists
        const snowStatsElement = document.getElementById('snowStats');
        if (snowStatsElement) {
            if (this.behavior === 'fireworks') {
                snowStatsElement.innerHTML = `
                    <div>Rockets: ${this.rockets.length}</div>
                    <div>Sparks: ${this.sparks.length}</div>
                    <div>Launches: ${this.totalFlakesSpawned}</div>
                    <div>Last break: ${this.lastFireworkEffectName || '—'}</div>
                    <div>Design: ${this.lastVolleyPatternName || '—'}</div>
                    <div>Pads: ${this.fireworkLaunchSites?.length || 0} (${
                        ['L', 'C', 'R']
                            .map((label, i) => {
                                const role = ['left', 'center', 'right'][i];
                                const n = this.fireworkLaunchSites?.filter((s) => s.role === role).length || 0;
                                return n ? `${label}×${n}` : null;
                            })
                            .filter(Boolean)
                            .join(' · ') || '—'
                    })</div>
                    <div>${this.fireworkFinaleActive ? 'FINALE' : `Volleys since finale: ${this.volleysSinceFinale || 0}`}</div>
                `;
            } else if (this.theme.combineEnabled) {
                snowStatsElement.innerHTML = `
                    <div>Total Snow Pieces: ${totalSnowPieces}</div>
                    <div>Largest Stack: ${largestChunk}</div>
                    <div>Stacks: ${chunksBySize[1]}x1 ${chunksBySize[2]}x2 ${chunksBySize[3]}x3 ${chunksBySize[4]}x4 ${chunksBySize[5]}x5 ${chunksBySize[6]}x6 ${chunksBySize[7]}x7 ${chunksBySize[8]}x8</div>
                `;
            } else {
                snowStatsElement.innerHTML = `
                    <div>Active particles: ${this.particles.length}</div>
                    <div>Spawned: ${this.totalFlakesSpawned}</div>
                `;
            }
        }
        
        // Calculate and display FPS
        const currentTime = performance.now();
        if (!this.lastFrameTime) {
            this.lastFrameTime = currentTime;
            this.frameCount = 0;
        }
        
        this.frameCount++;
        
        // Update FPS every second
        if (currentTime - this.lastFrameTime >= 1000) {
            const fps = Math.round(this.frameCount * 1000 / (currentTime - this.lastFrameTime));
            const fpsElement = document.getElementById('fps');
            if (fpsElement) {
                fpsElement.textContent = fps;
            }
            
            // Reset counters
            this.lastFrameTime = currentTime;
            this.frameCount = 0;
        }
        
        // Draw variance envelope visualization
        this.drawVarianceEnvelope();
    }
    
    drawVarianceEnvelope() {
        const canvas = document.getElementById('varianceEnvelope');
        if (!canvas) return;
        
        const ctx = canvas.getContext('2d');
        const width = canvas.width;
        const height = canvas.height;
        
        // Clear canvas
        ctx.fillStyle = '#1a1a2e';
        ctx.fillRect(0, 0, width, height);
        
        // Get current values from HTML display elements (these show the actual active values)
        const currentFlakesElement = document.getElementById('currentFlakesPerMinute');
        const currentFlakes = currentFlakesElement ? parseInt(currentFlakesElement.textContent) || this.baseFlakesPerMinute : this.baseFlakesPerMinute;
        const currentWind = this.windStrength; // This one is actually updated in the class
        const baseFlakes = this.baseFlakesPerMinute;
        const baseWind = this.baseWindStrength;
        const windVariance = this.windVariance;
        const snowfallVariance = this.snowfallVariance;
        
        // Scale factors to fit in canvas (center-based coordinate system)
        const flakesScale = (width * 0.8) / 6000; // Use 80% of width for flakes range
        const windScale = (height * 0.8) / 50; // Use 80% of height for wind range
        
        // Dynamic origin based on current base values (moves when user adjusts sliders)
        const originX = width / 2 + (baseFlakes - 3000) * flakesScale; // Center around 3000 flakes
        const originY = height / 2 - (baseWind - 0) * windScale; // Center around 0 wind
        
        // Draw coordinate grid with better labels and range indicators
        ctx.strokeStyle = '#4a4a6a';
        ctx.lineWidth = 1;
        ctx.setLineDash([2, 2]);
        
        // Vertical line (flakes) - dynamic origin line
        ctx.beginPath();
        ctx.moveTo(originX, 0);
        ctx.lineTo(originX, height);
        ctx.stroke();
        
        // Horizontal line (wind) - dynamic origin line
        ctx.beginPath();
        ctx.moveTo(0, originY);
        ctx.lineTo(width, originY);
        ctx.stroke();
        
        // Draw range grid lines to show the full scale
        ctx.strokeStyle = '#3a3a5a';
        ctx.lineWidth = 0.5;
        
        // Vertical range lines (every 500 flakes)
        for (let flakes = 0; flakes <= 6000; flakes += 500) {
            const x = originX + (flakes - baseFlakes) * flakesScale;
            if (x >= 0 && x <= width) {
                ctx.beginPath();
                ctx.moveTo(x, 0);
                ctx.lineTo(x, height);
                ctx.stroke();
                
                // Add flakes value labels
                ctx.fillStyle = '#6a6a8a';
                ctx.font = '8px Arial';
                ctx.textAlign = 'center';
                ctx.fillText(flakes.toString(), x, height - 8);
            }
        }
        
        // Horizontal range lines (every 10 wind units)
        for (let wind = -25; wind <= 25; wind += 10) {
            const y = originY - (wind - baseWind) * windScale;
            if (y >= 0 && y <= height) {
                ctx.beginPath();
                ctx.moveTo(0, y);
                ctx.lineTo(width, y);
                ctx.stroke();
                
                // Add wind value labels
                ctx.fillStyle = '#6a6a8a';
                ctx.font = '8px Arial';
                ctx.textAlign = 'right';
                ctx.fillText(wind.toString(), width - 5, y + 3);
            }
        }
        
        ctx.setLineDash([]);
        
        // Draw variance envelope (ellipse) centered at dynamic origin
        if (this.stormVarianceEnabled && (windVariance > 0 || snowfallVariance > 0)) {
            const envelopeWidth = (snowfallVariance * 2) * flakesScale;
            const envelopeHeight = (windVariance * 2) * windScale;
            
            ctx.strokeStyle = '#4CAF50';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.ellipse(originX, originY, envelopeWidth / 2, envelopeHeight / 2, 0, 0, 2 * Math.PI);
            ctx.stroke();
            
            // Fill with semi-transparent green
            ctx.fillStyle = 'rgba(76, 175, 80, 0.1)';
            ctx.fill();
            
            // Draw range indicator lines from origin to ellipse edges
            ctx.strokeStyle = '#4CAF50';
            ctx.lineWidth = 1;
            ctx.setLineDash([3, 3]);
            
            // Left edge (minimum flakes)
            const leftEdgeX = originX - envelopeWidth / 2;
            ctx.beginPath();
            ctx.moveTo(leftEdgeX, 0);
            ctx.lineTo(leftEdgeX, height);
            ctx.stroke();
            
            // Right edge (maximum flakes)
            const rightEdgeX = originX + envelopeWidth / 2;
            ctx.beginPath();
            ctx.moveTo(rightEdgeX, 0);
            ctx.lineTo(rightEdgeX, height);
            ctx.stroke();
            
            // Top edge (maximum wind)
            const topEdgeY = originY - envelopeHeight / 2;
            ctx.beginPath();
            ctx.moveTo(0, topEdgeY);
            ctx.lineTo(width, topEdgeY);
            ctx.stroke();
            
            // Bottom edge (minimum wind)
            const bottomEdgeY = originY + envelopeHeight / 2;
            ctx.beginPath();
            ctx.moveTo(0, bottomEdgeY);
            ctx.lineTo(width, bottomEdgeY);
            ctx.stroke();
            
            ctx.setLineDash([]);
            
            // Add range labels at the edges
            ctx.fillStyle = '#4CAF50';
            ctx.font = '9px Arial';
            ctx.textAlign = 'center';
            
            // Flakes range labels
            const minFlakes = baseFlakes - snowfallVariance;
            const maxFlakes = baseFlakes + snowfallVariance;
            ctx.fillText(`${minFlakes}`, leftEdgeX, height - 15);
            ctx.fillText(`${maxFlakes}`, rightEdgeX, height - 15);
            
            // Wind range labels
            ctx.textAlign = 'right';
            const minWind = baseWind - windVariance;
            const maxWind = baseWind + windVariance;
            ctx.fillText(`${minWind.toFixed(1)}`, width - 15, bottomEdgeY + 3);
            ctx.fillText(`${maxWind.toFixed(1)}`, width - 15, topEdgeY + 3);
        }
        
        // Draw base position (green dot) - at dynamic origin
        ctx.fillStyle = '#4CAF50';
        ctx.beginPath();
        ctx.arc(originX, originY, 4, 0, 2 * Math.PI);
        ctx.fill();
        
        // Calculate target position for smooth movement
        const targetX = originX + (currentFlakes - baseFlakes) * flakesScale;
        const targetY = originY - (currentWind - baseWind) * windScale; // Invert Y so positive wind is up
        
        // Smoothly interpolate dot position
        this.smoothDotTargetX = targetX;
        this.smoothDotTargetY = targetY;
        
        // Update smooth position
        this.smoothDotX += (this.smoothDotTargetX - this.smoothDotX) * this.smoothDotSpeed;
        this.smoothDotY += (this.smoothDotTargetY - this.smoothDotY) * this.smoothDotSpeed;
        
        // Draw current position (blue dot) - using smooth position
        ctx.fillStyle = '#2196F3';
        ctx.beginPath();
        ctx.arc(this.smoothDotX, this.smoothDotY, 4, 0, 2 * Math.PI);
        ctx.fill();
        
        // Draw line from base to current
        ctx.strokeStyle = '#FF9800';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(originX, originY);
        ctx.lineTo(this.smoothDotX, this.smoothDotY);
        ctx.stroke();
        
        // Add main axis labels
        ctx.fillStyle = '#FFFFFF';
        ctx.font = '12px Arial';
        ctx.textAlign = 'center';
        
        // X-axis label (flakes)
        ctx.fillText('Flakes per Minute', width / 2, height - 5);
        
        // Y-axis label (wind)
        ctx.save();
        ctx.translate(15, height / 2);
        ctx.rotate(-Math.PI / 2);
        ctx.fillText('Wind Strength', 0, 0);
        ctx.restore();
        
        // Add origin indicator
        ctx.fillStyle = '#FF9800';
        ctx.font = '10px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('Origin', originX, originY - 10);
        
        // Update variance info display with 1 decimal place for wind
        const baseInfoElement = document.getElementById('varianceBaseInfo');
        const currentInfoElement = document.getElementById('varianceCurrentInfo');
        
        if (baseInfoElement) {
            baseInfoElement.textContent = `${baseFlakes} flakes, ${baseWind.toFixed(1)} wind`;
        }
        
        if (currentInfoElement) {
            currentInfoElement.textContent = `${currentFlakes} flakes, ${currentWind.toFixed(1)} wind`;
        }
    }

    render() {
        this.ctx.fillStyle = this.theme?.skyColor || '#1a1a2e';
        this.ctx.fillRect(0, 0, this.width, this.height);
        
        // Render clouds first (background) using bitmap templates
        for (const cloud of this.clouds) {
            const gridX = Math.floor(cloud.x / this.gridSize);
            const gridY = Math.floor(cloud.y / this.gridSize);
            
            // Define color palette for each cloud type
            const palette = this.behavior === 'fireworks'
                ? { top: '#3a3a55', middle: '#2a2a40', bottom: '#1e1e30' }
                : {
                    top: '#FFFFFF',
                    middle: '#F6F6F6',
                    bottom: '#ECECEC'
                };
            
            this.paintCloud(this.ctx, this.gridSize, gridX, gridY, cloud.templateName, palette);
        }

        // Fireworks sit behind the village (farther Z) — houses/trees occlude them
        if (this.behavior === 'fireworks') {
            this.renderFireworks();
            this.renderFireworkLaunchPads();
        }
        
        // Render structures in front of the show
        for (let y = 0; y < this.grid.length; y++) {
            for (let x = 0; x < this.grid[0].length; x++) {
                const cell = this.grid[y][x];
                if (cell.type === 'structure') {
                    // Draw each structure cell as a grid cell that scales with gridSize
                    const drawX = x * this.gridSize;
                    const drawY = y * this.gridSize;
                    
                    switch (cell.data.type) {
                        case 'house':
                            // Draw house body as a simple grid cell
                            this.ctx.fillStyle = cell.data.color;
                            this.ctx.fillRect(drawX, drawY, this.gridSize, this.gridSize);
                            break;
                            
                        case 'roof':
                            // Draw roof with different styles and better visual effects
                            this.ctx.fillStyle = cell.data.color;
                            this.ctx.fillRect(drawX, drawY, this.gridSize, this.gridSize);
                            
                            // Add subtle shading to make roofs look more 3D
                            this.ctx.fillStyle = 'rgba(0,0,0,0.1)';
                            this.ctx.fillRect(drawX, drawY, this.gridSize, this.gridSize);
                            break;
                            
                        case 'door':
                            // Draw door as a simple grid cell
                            this.ctx.fillStyle = '#654321';
                            this.ctx.fillRect(drawX, drawY, this.gridSize, this.gridSize);
                            break;
                            
                        case 'window':
                            // Draw window as a simple grid cell
                            this.ctx.fillStyle = '#87CEEB';
                            this.ctx.fillRect(drawX, drawY, this.gridSize, this.gridSize);
                            break;
                            
                        case 'tree':
                            // Draw tree as a simple grid cell
                            this.ctx.fillStyle = cell.data.color;
                            this.ctx.fillRect(drawX, drawY, this.gridSize, this.gridSize);
                            break;
                            
                        case 'christmas-light':
                            // Draw Christmas light as a simple colored square
                            this.ctx.fillStyle = cell.data.lightColor;
                            this.ctx.fillRect(drawX, drawY, this.gridSize, this.gridSize);
                            break;
                            
                        case 'chimney':
                            // Draw chimney using its stored color
                            this.ctx.fillStyle = cell.data.color;
                            this.ctx.fillRect(drawX, drawY, this.gridSize, this.gridSize);
                            break;
                            
                        case 'ground':
                            // Draw ground as a simple grid cell
                            this.ctx.fillStyle = cell.data.color;
                            this.ctx.fillRect(drawX, drawY, this.gridSize, this.gridSize);
                            break;
                            
                        default:
                            // Default: draw as a simple grid cell with stored color
                            this.ctx.fillStyle = cell.data.color || '#888888';
                            this.ctx.fillRect(drawX, drawY, this.gridSize, this.gridSize);
                            break;
                    }
                }
            }
        }

        if (this.behavior !== 'fireworks') {
            // Render particles on top
            for (const particle of this.particles) {
                const alpha = particle.life != null ? Math.max(0.15, particle.life) : 1;
                this.ctx.globalAlpha = alpha;
                this.ctx.fillStyle = particle.color;
                // Draw particle at exact grid position
                const drawX = Math.floor(particle.x / this.gridSize) * this.gridSize;
                const drawY = Math.floor(particle.y / this.gridSize) * this.gridSize;
                
                // Draw snow particles as regular grid cells (size affects color, not visual size)
                this.ctx.fillRect(drawX, drawY, this.gridSize, this.gridSize);
            }
            this.ctx.globalAlpha = 1;
        }
        
        // Draw wind vectors on top
        this.drawWindVectors();
    }

    start() {
        let lastTime = 0;
        const gameLoop = (currentTime) => {
            const deltaTime = currentTime - lastTime;
            lastTime = currentTime;
            
            if (this.isRunning) {
                this.update(deltaTime);
            }
            this.render();
            
            requestAnimationFrame(gameLoop);
        };
        requestAnimationFrame(gameLoop);
    }

    addPixelArtClouds() {
        // Initial clouds should spawn randomly across the horizon
        this.spawnInitialClouds();
    }
    
    addMobileClouds() {
        // On mobile, only add high-altitude CIRRUS clouds (1-2 clouds)
        const numCirrusClouds = Math.floor(Math.random() * 2) + 1; // 1-2 clouds
        
        for (let i = 0; i < numCirrusClouds; i++) {
            this.spawnInitialCloud('CIRRUS');
        }
        
        // Update counts after initial spawn
        this.updateCloudCounts();
    }
    
    spawnInitialClouds() {
        // Spawn initial clouds randomly across the screen for each cloud type
        Object.keys(this.cloudSettings).forEach(templateName => {
            const settings = this.cloudSettings[templateName];
            const targetCount = settings.targetCount;
            
            for (let i = 0; i < targetCount; i++) {
                this.spawnInitialCloud(templateName);
            }
        });
        
        // Update counts after initial spawn
        this.updateCloudCounts();
    }
    
    spawnInitialCloud(templateName) {
        const settings = this.cloudSettings[templateName];
        const minY = settings.minY;
        const maxY = settings.maxY;
        
        // Random position within height range
        const cloudY = Math.floor((Math.random() * (maxY - minY) + minY) / this.gridSize) * this.gridSize;
        
        // Random width and height based on template
        let cloudWidth, cloudHeight, parallaxSpeed;
        
        if (templateName === 'S1') {
            cloudWidth = Math.floor((Math.random() * 40 + 80) / this.gridSize) * this.gridSize; // 80-120px
            cloudHeight = Math.floor((Math.random() * 20 + 20) / this.gridSize) * this.gridSize; // 20-40px
            parallaxSpeed = 0.003;
        } else if (templateName === 'M1') {
            cloudWidth = Math.floor((Math.random() * 40 + 120) / this.gridSize) * this.gridSize; // 120-160px
            cloudHeight = Math.floor((Math.random() * 30 + 60) / this.gridSize) * this.gridSize; // 60-90px
            parallaxSpeed = 0.003;
        } else if (templateName === 'CIRRUS') {
            cloudWidth = Math.floor((Math.random() * 40 + 160) / this.gridSize) * this.gridSize; // 160-200px
            cloudHeight = Math.floor((Math.random() * 10 + 20) / this.gridSize) * this.gridSize; // 20-30px
            parallaxSpeed = 0.001;
        }
        
        // Random X position across the screen (not off-screen for initial spawn)
        const cloudX = Math.floor((Math.random() * (this.width - cloudWidth)) / this.gridSize) * this.gridSize;
        
        this.createPixelArtCloud(cloudX, cloudY, cloudWidth, cloudHeight, parallaxSpeed, templateName);
    }
    
    addCloudLayer(minCount, maxCount, minWidth, maxWidth, minHeight, maxHeight, parallaxSpeed, layerType, minY, maxY) {
        const numClouds = Math.floor(Math.random() * (maxCount - minCount + 1)) + minCount;
        
        for (let i = 0; i < numClouds; i++) {
            // Add some randomness to positioning - clouds shouldn't be perfectly aligned
            const randomOffsetX = (Math.random() - 0.5) * 100; // Â±50px horizontal variation
            const randomOffsetY = (Math.random() - 0.5) * 60;  // Â±30px vertical variation
            
            const cloudX = Math.floor((Math.random() * (this.width - maxWidth) + randomOffsetX) / this.gridSize) * this.gridSize;
            const cloudY = Math.floor((Math.random() * (maxY - minY) + minY + randomOffsetY) / this.gridSize) * this.gridSize; // Use minY/maxY for height positioning
            
            // Random cloud sizes within layer bounds with some variation
            const cloudWidth = Math.floor((Math.random() * (maxWidth - minWidth) + minWidth + (Math.random() - 0.5) * 20) / this.gridSize) * this.gridSize;
            const cloudHeight = Math.floor((Math.random() * (maxHeight - minHeight) + minHeight + (Math.random() - 0.5) * 15) / this.gridSize) * this.gridSize;
            
            this.createPixelArtCloud(cloudX, cloudY, cloudWidth, cloudHeight, parallaxSpeed, layerType);
        }
    }
    
    createPixelArtCloud(x, y, width, height, parallaxSpeed, layerType) {
        // Use bitmap templates for clouds
        const gridX = Math.floor(x / this.gridSize);
        const gridY = Math.floor(y / this.gridSize);
        
        // Add cloud to clouds array for movement and collision
        this.clouds.push({
            x: x,
            y: y,
            gridX: gridX,
            gridY: gridY,
            width: width,
            height: height,
            parallaxSpeed: parallaxSpeed,
            layerType: layerType,
            templateName: layerType,
            color: '#FFFFFF' // Default color, will be overridden by paintCloud
        });
    }


    

    

    
    // Helper method to adjust color brightness
    adjustColorBrightness(color, amount) {
        // Convert hex to RGB, adjust brightness, convert back
        const hex = color.replace('#', '');
        const r = Math.max(0, Math.min(255, parseInt(hex.substr(0, 2), 16) + amount));
        const g = Math.max(0, Math.min(255, parseInt(hex.substr(2, 2), 16) + amount));
        const b = Math.max(0, Math.min(255, parseInt(hex.substr(4, 2), 16) + amount));
        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    }

    applyWindEffects() {
        // Apply wind to clouds by moving them in the separate array
        this.moveCloudsWithWind();
        
        // Wind is now handled in the particle movement system
        // No need to manually move particles here
    }
    
    moveCloudsWithWind() {
        if (Math.abs(this.windStrength) < 0.01) return; // No wind, no movement
        
        // Move clouds based on wind strength and their parallax speeds
        for (const cloud of this.clouds) {
            // Calculate movement based on wind and parallax speed
            const parallaxSpeed = cloud.parallaxSpeed;
            // Use the actual parallax speed directly - no additional multipliers
            const windOffset = this.windStrength * parallaxSpeed;
            
            if (Math.abs(windOffset) > 0.001) { // Lower threshold for very slow clouds
                // Accumulate movement over time instead of using Math.sign
                cloud.movementAccumulator = (cloud.movementAccumulator || 0) + windOffset;
                
                // Different movement thresholds based on cloud layer
                let movementThreshold = 0.5; // Default for close/medium clouds
                if (cloud.layerType === 'distant') {
                    movementThreshold = 2.0; // Distant clouds need 4x more accumulation to move
                }
                
                // Only move when we've accumulated enough movement for a full grid cell
                if (Math.abs(cloud.movementAccumulator) >= movementThreshold) {
                    const moveDirection = Math.sign(cloud.movementAccumulator);
                    const newGridX = cloud.gridX + moveDirection;
                    
                    // Handle wrapping when clouds go off-screen
                    let finalGridX = newGridX;
                    if (newGridX < 0) {
                        finalGridX = Math.floor(this.width / this.gridSize) - 1; // Wrap to right edge
                    } else if (newGridX >= Math.floor(this.width / this.gridSize)) {
                        finalGridX = 0; // Wrap to left edge
                    }
                    
                    // Update cloud's grid position
                    cloud.gridX = finalGridX;
                    cloud.x = finalGridX * this.gridSize;
                    
                    // Reset accumulator after moving
                    cloud.movementAccumulator = 0;
                }
            }
        }
    }
}

// Export SnowSimulation class for ES6 imports
export { SnowSimulation };

// Also make it available globally for backward compatibility if needed
if (typeof window !== 'undefined') {
    window.SnowSimulation = SnowSimulation;
} 